(ns ductile.ui.fahrzeug
  (:require ["@radix-ui/react-dropdown-menu" :as DropdownMenu]
            ["@turf/turf" :as turf]
            [applied-science.js-interop :as j]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.base64u :as base64u]
            [ductile.clerk.viewer :as dcv]
            [ductile.schema :as schema]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr trn]]
            [ductile.ui.mapbox :as mapbox]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.pred-view :as pred-view]
            [ductile.ui.router :as router]
            [ductile.ui.routes :as routes]
            [ductile.ui.scope]
            [ductile.ui.util :as ui.util]
            [ductile.ui.view-stack :as view-stack]
            [ductile.util :as util]
            [ductile.workflow.parking-utils :as parking-utils]
            [nextjournal.clerk-table-stats :as table-stats]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.viewer :as viewer]
            [nextjournal.log :as log]
            [re-frame.context]
            [reagent.core :as r])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]
                   [ductile.ui.scope.macros :refer [with-scope-protected-value when-current-user-scope]]))

(defn fahrzeug-id->query [fahrzeug-id]
  (let [[identifier-type id] (str/split (cond->> fahrzeug-id
                                          (not (str/includes? fahrzeug-id "--")) (str "vin--"))
                                        #"\-\-")]
    (case identifier-type
      "vin" {:vehicle/vin id}
      "ars" {:ars/id id}
      (throw (ex-info "unexpected identifier-type" {:fahrzeug-id fahrzeug-id :identifier-type identifier-type})))))

(defn ->fahrzeug-id [identifier-type id]
  (case identifier-type
    "vin" id
    "ars" (str identifier-type "--" id)
    (throw (ex-info "unexpected identifier-type" {:identifier-type identifier-type :id id}))))

(defn bmw-location->description [location-name]
  (let [loc-prefix (if (< 3 (count location-name)) (subs location-name 0 2) location-name)]
    (condp = loc-prefix
      "01"  (str (tr "Compound") ": " (subs location-name 2) " Compound")
      "55"  (str (tr "Port") ": " (subs location-name 2) " Port")
      "60"  (str (tr "Port") ": " (subs location-name 2) " Port")
      "50"  (str (tr "Dealer") ": " (subs location-name 2))
      location-name)))

(defn location [e]
  [:span
   (when-let [bmw-location-codes (:external.bmw/location-codes e)]
     [ui/badge {:variant :secondary
                :class "uppercase mr-2"}
      (str "BMW@" (str/join "+" (map bmw-location->description bmw-location-codes)))])
   (when-let [kia-location-code (:external.kia/location-codes e)]
     [ui/badge {:variant :secondary
                :class "uppercase mr-2"}
      (str "KIA@" (str/join "+" kia-location-code))])
   (str/join " – " (remove nil? [(or (:compound/name e)
                                     (:location/name e)
                                     (:address/name e))
                                 (:address/street e)
                                 (str/join " " [(:address/postcode e) (:address/city e)])
                                 (:address/province-code e)]))])

(defn service-details [{:as service :service/keys [code unit] :destination/keys [location]}]
  (let [details (remove nil?
                        [(when-let [voltage (:vehicle.status/battery-voltage service)]
                           (str voltage " " (tr unit)))
                         (when-let [status (:vehicle.status/battery-status service)]
                           (tr status))
                         (when-let [charge (:vehicle.status/high-voltage-battery-charge service)]
                           (str charge " " (tr unit)))
                         (when (and location (contains? #{"Lagerabruf" "Aktualisierter Lagerabruf" "Call-off" "Aggiornamento Call-off"} code))
                           (let [columns [:address/name :address/street :address/postcode :address/city :external.bmw/location-codes :external.kia/location-codes]]
                             [render/inspect (viewer/table {:nextjournal.clerk/render-opts {:column-order columns}}
                                                           [(select-keys location columns)])]))
                         (when-let [equipment (:vehicle/equipment service)]
                           (str/join ", " equipment))
                         (when-let [comments (not-empty (:ductile/comments service))]
                           (into [:div.flex.flex-wrap.gap-3]
                                 (map (fn [{:as comment :ductile.comment/keys [kind]}]
                                        (case kind
                                          :ductile.comment.kind/image [mobile/image-tile {:url (str "/cas/" (:cas/multihash comment))}]
                                          (do (js/console.log "Unsupported comment kind in workflow/service-details: " kind)
                                              (str comment)))))
                                 comments))
                         (when-let [quantity (:service/quantity service)]
                           (cond-> quantity
                             unit
                             (str " " (tr unit))
                             (:fuel/type service)
                             (str " " (tr (:fuel/type service)))))
                         (when-let [result (:service/result service)]
                           (when (contains? result :damaged?)
                             (if (:damaged? result) (tr "Damages found") (tr "No damages found"))))])]
    (when (not-empty details)
      (into [:div.flex.flex-col.gap-2]
            (map (fn [info] [:div info]))
            details))))

(defn new-service-form [{:as fahrzeug :fahrzeug/keys [assignable-services]}]
  (r/with-let [sorted-services (sort-by (apply juxt (concat [:service/code] schema/requested-keys))
                                        assignable-services)]
    [:div.bg-white.rounded-lg.shadow-lg
     [:div.flex.justify-between.items-center.py-4.px-6.border-b
      [:h4.mt-0.text-base.font-bold (tr "Add new service")]]
     [:div [ui/error-view @(deframe/!route-get-in [:fetch :error])]]
     [:div.bg-slate-50.py-4.px-6
      [combobox/view
       {:choices (map (fn [service] {:ui/value (ui/order-service-label service)
                                     :ui/label (ui/order-service-label service)}) sorted-services)
        :value @(deframe/!route-get ::selected-service)
        :on-change #(deframe/route-assoc! ::selected-service (:ui/label %))
        :placeholder (tr "Select a service")
        :size 3}]]
     [:div.px-6.py-4.md:flex.justify-between.items-center.border-t
      [ui/button {:variant :primary
                  :size 3
                  :class "flex-shrink-0 flex-auto"
                  :disabled (str/blank? @(deframe/!route-get ::selected-service ""))
                  :on-click #(deframe/call-lambda {:on-success (fn [] (deframe/route-dissoc! ::selected-service))}
                                                  'ductile.workflow/order-service
                                                  (-> (some (fn [service]
                                                              (and (= @(deframe/!route-get ::selected-service "")
                                                                      (ui/order-service-label service))
                                                                   service))
                                                            assignable-services)
                                                      (select-keys (concat schema/requested-keys
                                                                           [:service/elog-code :service/code :service/unit :service/contract]))
                                                      (assoc :ductile/id (:ductile/id fahrzeug))))}
       (tr "Add service")]]]))

(defn block-types [current-vehicle-blocks-set]
  (cond-> [:block/by-compound
           :block/by-customer]
    (and (or (ui.util/has-required-scope? :scope/developer)
             (ui.util/has-required-scope? :scope/edi))
         ;; only allow unblocking an EDI block and not reenabling an EDI block
         (contains? current-vehicle-blocks-set :block/by-edi))
    (conj :block/by-edi)))

(defn blocks-dropdown [{:as fahrzeug :vehicle/keys [blocks]} {:keys [editable?]}]
  [:> DropdownMenu/Root
   [:> DropdownMenu/Trigger
    (let [button-loading? (= 'ductile.workflow.engine/handle-block @(deframe/!route-get-in [:fetch :loading]))]
      ;; TODO: enable loading behaviour as part of button
      [ui/button
       {:disabled? (or button-loading? (:exit/datetime fahrzeug))
        :size 1
        :variant :destructive}
       (when button-loading? [ui/spinner {:size 16 :class "ml-2"}])
       (tr "Manage blocks")
       [ui/icon :chevron-down]])]
   [:> DropdownMenu/Portal
    [:> DropdownMenu/Content
     {:class-name "min-w-[220px] bg-white rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
      :side-offset 7}
     [:> DropdownMenu/Arrow {:class "fill-white"}]
     (into [:<>]
           (map (fn [block-type]
                  [:> DropdownMenu/CheckboxItem
                   {:class-name "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
                    :checked (contains? (set blocks) block-type)
                    :on-select (fn [event]
                                 (.preventDefault event)
                                 (.stopPropagation event))
                    :on-checked-change (fn [checked?]
                                         ;; TODO: Make it use manage-blocks and block-type
                                         (when  (js/confirm (if checked? (tr "Are you sure you want to block this car (%1)?" (tr block-type))
                                                                (tr "Are you sure you want to unblock this car (%1)?" (tr block-type))))
                                           (deframe/call-lambda 'ductile.workflow.engine/handle-manage-blocks
                                                                {:vehicle-search-params {:search-params {:ductile/id (:ductile/id fahrzeug)}}
                                                                 :compound-name (:compound/name fahrzeug)
                                                                 :block-type block-type
                                                                 :action (if checked?
                                                                           :block/add
                                                                           :block/remove)})))}
                   [:> DropdownMenu/ItemIndicator
                    {:class-name "absolute left-0 w-[25px] inline-flex items-center justify-center"}
                    [ui/icon :check]]
                   (tr block-type)]))
           (block-types (set blocks)))]]])

;; TODO: dedupe with ductile.load-builder/schnelldreher? and unify with util/has-service?
(defn package-badge [{:as fahrzeug :ui/keys [size] :or {size 1}}]
  [:<>
   (when (= "BMW Aldi" (:contract/name fahrzeug))
     [ui/badge {:size size :variant :white-green} "Paket Aldi"])
   (when (= "LEAN Timmermanns" (:contract/name fahrzeug))
     [ui/badge {:size size :variant :white-green} "Paket LEAN"])
   (when (= :self-pickup (:dispo/flag fahrzeug))
     [ui/badge {:size size :variant :white-green} (tr :self-pickup)])
   (when (contains? (:fahrzeug/labels fahrzeug) :label/tnr-mini)
     [ui/badge {:size size :variant :white-green} "TNR Mini"])
   (when (contains? (:fahrzeug/labels fahrzeug) :label/all-risk-insurance)
     [ui/badge {:size size :variant :white-green} "All-Risk"])
   (when (contains? (:fahrzeug/labels fahrzeug) :label/quick-turnaround)
     [ui/badge {:size size :variant :white-green} (tr :label/quick-turnaround)])])

(defn engine-badge [{:vehicle/keys [engine-type] :ui/keys [size]}]
  (when engine-type
    [ui/badge {:variant :gray :size (or size 1)}
     (tr engine-type)]))

(defn flatten-map
  "Turn a nested map into a flat map with paths as keys"
  [map]
  (reduce (fn [acc [k v]]
            (if (map? v)
              (merge acc
                     (update-keys (flatten-map v)
                                  #(vec (concat [k] %))))
              (assoc acc [k] v)))
          {}
          map))

(defn expected-error-view
  ([error] [expected-error-view error []])
  ([error items]
   [mobile/table
    {:separated? false
     :items (concat
             [[:li.bg-yellow-100.border-t.border-b.py-2.px-3.expected-error
               {:class "-mt-[33px]"
                :data-testid "expected-error"}
               [:div.flex.gap-3.items-center
                [:div.leading-none.drop-shadow {:class "text-[30px]"} "⚠️"]
                (into [:div]
                      (if-let [errors
                               (->> [(tr (:error-message error))
                                     (when-let [error (:validation/errors error)]
                                       (into [:<>]
                                             (map (fn [[path errors]]
                                                    [:div
                                                     [:span.font-bold.mr-1 (->> path
                                                                                (map (fn [field] (tr field)))
                                                                                (str/join " > ")) ":"]
                                                     (str/join ", " (map tr errors))]))
                                             (flatten-map error)))
                                     (when (:missing-keys error)
                                       [:div.font-bold
                                        (str/join ", " (map tr (:missing-keys error)))])
                                     (when (string? error) (tr error))
                                     (when (string? (:error error)) (tr (:error error)))]
                                    (keep identity)
                                    not-empty)]
                        errors
                        [(pr-str error)]))]]]
             items)}]))

(defn barcode-button
  ([fahrzeug] [barcode-button {} fahrzeug])
  ([{size :size} fahrzeug]
   (when (and (ui/debug-mode?) (not ui/is-mobile?))
     [:div
      [ui/tooltip {:content [:div
                             [:div.gap-2.bg-white.p-2.flex.mb-2.rounded.flex-wrap.justify-center
                              [:div [ui/code39 {:width 430} (:vehicle/vin fahrzeug)]]
                              (when (:ars/id fahrzeug) [ui/qr-code {:width 70} (:ars/id fahrzeug)])]
                             [:div.select-text.flex.flex-col.gap-1.pl-3.pb-2.text-sm.text-left
                              [:div (tr :vehicle/vin) ": " [ui/vin (:vehicle/vin fahrzeug)]]
                              [:div (tr :ars/id) ": " [:span.tabular-nums (:ars/id fahrzeug)]]]]
                   :side "right"
                   :variant :white}
       [:div.bg-white.border.border-slate-300.rounded.px-1.flex.items-center
        {:class (case size
                  :sm "h-[20px] text-[9px]"
                  "h-[30px] text-[12px]")}
        [ui/icon {:size (case size :sm 16 24)} :scan]]]])))

(defn vehicle-details-header
  ([fahrzeug] (vehicle-details-header nil fahrzeug))
  ([{:as opts :keys [editable? details? standalone? href on-click size layout] :or {size :responsive details? true}}
    {:as fahrzeug :fahrzeug/keys [destination-error] :vehicle/keys [length-mm  width-mm  height-mm weight volume equipment engine-type]}]
   (when (some #(get fahrzeug %) [:contract/name :color/description :make/name :vehicle/model :exit/transport-order :vehicle/vin :vehicle/engine-type :fahrzeug/destination-error])
     (let [missing-information (contains? (:fahrzeug/labels fahrzeug) :label/missing-information)
           details? (and details?
                         (not on-click)
                         (not href)
                         (or length-mm  width-mm  height-mm weight volume equipment (:ars/id fahrzeug)))
           expanded? (and details? @(deframe/!get ::vehicle-details-header-expanded?))
           blocks (:vehicle/blocks fahrzeug)]
       [(if href :a :div) {:class (concat
                                   (cond
                                     (= size :sm) ["text-[11px]"]
                                     (= size :responsive) ["text-[11px]" "sm:text-sm"]))
                           :href href
                           :on-click on-click}
        [:div {:class (concat [(when (and (empty? blocks) (not standalone?)) "border-b")]
                              (when (and (not= layout :prioritize-vin) (not standalone?))
                                ["bg-gray-100" "border-slate-300"]))}
         [:div.flex.gap-3.flex-col.flex-wrap
          {:class (cond
                    (= size :sm) ["p-2"]
                    (= size :responsive) ["p-2" "md:p-3" "lg:flex-row"])}
          [:div.flex
           [:div
            [:div.items-center.flex.border.border-slate-300.bg-white.rounded.overflow-hidden
             {:class (cond-> ["p-0.5"]
                       (= size :sm) (concat ["w-10" "h-10"])
                       (= size :responsive) (concat ["w-10" "h-10" "md:w-12" "md:h-12"]))}
             [ui/make-logo (:make/name fahrzeug)]]]

           [:div
            [:div.flex.flex-col.justify-center.ml-2
             {:class (cond
                       (= size :sm) ["min-h-10"]
                       (= size :responsive) ["min-h-10" "md:min-h-12"])}
             (when (or (:make/name fahrzeug) (:vehicle/model fahrzeug) (:color/gradient-from fahrzeug) (and (:vehicle/vin fahrzeug) (= :prioritize-vin layout)))
               [:<>
                (when (= :prioritize-vin layout)
                  [:div.text-sm.tabular-nums
                   {:class (when (or (:make/name fahrzeug) (:vehicle/model fahrzeug)) ["mb-1"])}
                   [ui/vin (:vehicle/vin fahrzeug)]])
                [:div.lg:flex.lg:items-center
                 [:div.flex.items-center.flex-wrap.gap-x-1
                  {:class (cond
                            (= size :sm) ["text-[12px]"]
                            (= size :responsive) ["text-[12px]" "md:text-base"])}
                  [:span (:make/name fahrzeug) " " [:span.font-bold (:vehicle/model fahrzeug)]]

                  (when (:color/gradient-from fahrzeug)
                    [:div.w-6.rounded-full.shrink-0
                     {:class "h-[0.85rem]"
                      :style {:background (str "linear-gradient(to right, " (:color/gradient-from fahrzeug) " , " (:color/gradient-to fahrzeug) ")")}}])

                  [barcode-button {:size size} fahrzeug]]]])

             [:div.flex.gap-1.empty:hidden.my-1
              [engine-badge fahrzeug]
              (when (contains? fahrzeug :vehicle/non-drivable?)
                (if (:vehicle/non-drivable? fahrzeug)
                  [ui/badge {:variant :red :size 1} (tr "Not drivable")]
                  [ui/badge {:variant :green :size 1} (tr "Drivable")]))
              [package-badge fahrzeug]]

             [:div
              {:class (cond-> []
                        (= size :sm) (concat ["leading-4"])
                        (= size :responsive) (concat ["leading-5" "sm:leading-6"])
                        (= layout :row) (concat ["flex" "items-center" "gap-2"]))}
              (when (:contract/name fahrzeug)
                [:div.flex.items-center.flex-wrap
                 (into [:span [:span.text-slate-600 (tr :contract/name) ": "]]
                       (->> [(:contract/name fahrzeug)
                             (str (get-in fahrzeug [:exit/transport-order :destination/location :address/postcode])
                                  " "
                                  (get-in fahrzeug [:exit/transport-order :destination/location :address/city]))]
                            (remove str/blank?)
                            (interpose " → ")))])
              (when (and (:vehicle/vin fahrzeug) (not= :prioritize-vin layout))
                [:div
                 [:span.text-slate-600 (tr :vehicle/vin) ": "]
                 [:span.tabular-nums [ui/vin (:vehicle/vin fahrzeug)]]])

              (when (:vehicle/license-plate fahrzeug)
                [:div
                 [:span.text-slate-600 (tr :vehicle/license-plate) ": "]
                 [:span.tabular-nums (:vehicle/license-plate fahrzeug)]])

              (when-let [{:parking-spot/keys [zone row place]} (:fahrzeug/parking-spot fahrzeug)]
                [:div
                 [:span.text-slate-600 (tr "Parking spot") ": "]
                 [:span.font-bold.tabular-nums (str/join "/" (filter some? [zone row place]))]])]]]

           (when details?
             [:div.ml-auto.lg:hidden
              [:button.p-1.sm:p-3.border.rounded.border-slate-300
               {:type :button
                :on-click (fn [] (deframe/assoc! ::vehicle-details-header-expanded? (not expanded?)))}
               [ui/icon {:class ["transition duration-25" (when expanded? "rotate-90")]} :chevron-left]]])]

          (let [buttons (remove nil?
                                [(when editable?
                                   [blocks-dropdown fahrzeug opts])
                                 (when-current-user-scope
                                  #{:scope/developer
                                    :scope/edi}
                                  (when details?
                                    [ui/button {:href (router/path '[edi dashboard] {} (str "b64=" (base64u/encode {:search-params {:vehicle/vin #{(:vehicle/vin fahrzeug)}}})))
                                                :size 1}
                                     (tr "Show EDI")]))
                                 (when details?
                                   [ui/button
                                    {:on-click (fn [] (deframe/assoc! ::vehicle-details-header-expanded? (not expanded?)))
                                     :size 1}
                                    (if expanded? (tr "Hide details") (tr "Show details"))])])]
            (when (or buttons (ui/debug-mode?))
              [:div.mx-auto.hidden.lg:block.mr-0.empty:hidden
               (when (seq buttons)
                 [:div.hidden.lg:flex.justify-center.lg:justify-end.gap-2.items-center.flex-wrap.empty:hidden
                  buttons])]))]

         (when (and details? expanded?)
           [:div.bg-white.leading-6.sm:leading-8.border-t.pl-6.py-1
            [:table.w-full
             [:tbody
              (when-let [ars-id (:ars/id fahrzeug)]
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "ARS-ID")]
                 [:td [:span.tabular-nums ars-id]]])
              (when (:color/description fahrzeug)
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "Color")]
                 [:td [:span.tabular-nums (:color/description fahrzeug)]]])
              (when (or length-mm  width-mm  height-mm)
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "L/W/H")]
                 [:td (str length-mm "mm / " width-mm "mm / " height-mm "mm")]])
              (when weight
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "Weight")]
                 [:td weight "kg"]])
              (when volume
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "Volume")]
                 [:td volume "m³"]])
              (when equipment
                [:tr.border-b.last:border-b-0
                 [:th.pr-1.text-slate-600.font-normal.text-left.align-top (tr "Equipment")]
                 [:td (tr (str/join ", " (sort equipment)))]])]]])]

        (when (and (or blocks missing-information) (not= layout :prioritize-vin))
          [:div.w-full.bg-red-700.text-white.uppercase.font-bold.flex.items-center.tracking-wide.text-xs.py-1
           {:class "px-2"}
           [:div.w-10.flex.justify-center.relative.overflow-visible
            [:div.absolute {:style {:top -12
                                    :left "50%"
                                    :transform "translateX(-50%)"
                                    :width 0
                                    :height 0
                                    :border-left "10px solid transparent"
                                    :border-right "10px solid transparent"
                                    :border-bottom "12px solid #b91c1c"}}] ; red-700
            [ui/icon {:size 24} :x-octagon]]
           [:div {:class "ml-1.5"} (cond blocks (str/join ", " (map tr blocks))
                                         missing-information (tr "missing information"))]])
        (when (and destination-error (not= layout :prioritize-vin))
          [expected-error-view destination-error])]))))

(defscene vehicle-details-header-card-single
  (let [fahrzeug {:ars/id "OAKBZDG2000120001200012000472034"
                  :contract/name "Kia Distribuzione"
                  :make/name "Kia"
                  :vehicle/model "Sportage (NQ5)"
                  :color/description "(USG) YUCCA STEEL GREY M"
                  :color/gradient-from "#ff1933"
                  :color/gradient-to "#090912"
                  :vehicle/non-drivable? false
                  :vehicle/license-plate "W-90272B"
                  :vehicle/vin "U5YPU81BASL375939"
                  :vehicle/weight 1234
                  :vehicle/length-mm 3000
                  :vehicle/width-mm 2000
                  :vehicle/height-mm 1500
                  :vehicle/volume 5
                  :vehicle/equipment ["Key Card" "Kennzeichen" "Schlüssel" "Pannendreieck"]
                  :vehicle/engine-type :engine.type/electric
                  :fahrzeug/parking-spot {:parking-spot/zone "Quarantäne SP3"
                                          :parking-spot/row "1"
                                          :parking-spot/place "2"}
                  :service.event/completed #{{:service/code "Paket Aldi"}}
                  :exit/transport-order {:destination/location
                                         {:location/longitude 16.781033
                                          :external.kia/location-codes ["07316"]
                                          :address/country #:country{:code "ITA"}
                                          :location/types [:location.type/dealer]
                                          :address/province-code "BA"
                                          :ductile/id #uuid "4cd08698-3bd1-4840-a462-1c1d1a010056"
                                          :address/postcode "70026"
                                          :address/street "SS 96 km 119400 Cda Lamarossa"
                                          :address/region "Puglia"
                                          :address/name "EUROMOTOR AUTOMOBILI S.r.l."
                                          :address/city "MODUGNO"
                                          :location/latitude 41.082865}}}]
    [mobile/scroll-content
     [mobile/navbar {:title "Eingangsinspektion" :back [mobile/back-link]}]

     [vehicle-details-header (assoc fahrzeug :vehicle/blocks nil #_#{:block/by-customer})]

     [mobile/table {:title "License Plate" :items [[:div.h-10]]}]]))

(defscene vehicle-details-header-card
  (let [fahrzeug {:ars/id "OAKBZDG2000120001200012000472034"
                  :contract/name "Kia Distribuzione"
                  :make/name "Kia"
                  :vehicle/model "Sportage (NQ5)"
                  :color/description "(USG) YUCCA STEEL GREY M"
                  :color/gradient-from "#ff1933"
                  :color/gradient-to "#090912"
                  :vehicle/non-drivable? false
                  :vehicle/license-plate "W-90272B"
                  :vehicle/vin "U5YPU81BASL375939"
                  :vehicle/weight 1234
                  :vehicle/length-mm 3000
                  :vehicle/width-mm 2000
                  :vehicle/height-mm 1500
                  :vehicle/volume 5
                  :vehicle/equipment ["Key Card" "Kennzeichen" "Schlüssel" "Pannendreieck"]
                  :vehicle/engine-type :engine.type/electric
                  :fahrzeug/parking-spot {:parking-spot/zone "Quarantäne SP3"
                                          :parking-spot/row "1"
                                          :parking-spot/place "2"}
                  :service.event/completed #{{:service/code "Paket Aldi"}}
                  :exit/transport-order {:destination/location
                                         {:location/longitude 16.781033
                                          :external.kia/location-codes ["07316"]
                                          :address/country #:country{:code "ITA"}
                                          :location/types [:location.type/dealer]
                                          :address/province-code "BA"
                                          :ductile/id #uuid "4cd08698-3bd1-4840-a462-1c1d1a010056"
                                          :address/postcode "70026"
                                          :address/street "SS 96 km 119400 Cda Lamarossa"
                                          :address/region "Puglia"
                                          :address/name "EUROMOTOR AUTOMOBILI S.r.l."
                                          :address/city "MODUGNO"
                                          :location/latitude 41.082865}}}]
    [mobile/scroll-content
     [mobile/navbar {:title "Eingangsinspektion" :back [mobile/back-link]}]
     [vehicle-details-header fahrzeug]
     [vehicle-details-header (assoc fahrzeug :vehicle/blocks #{:block/by-customer})]
     [vehicle-details-header (select-keys fahrzeug [:ars/id :vehicle/vin])]
     [vehicle-details-header (merge (select-keys fahrzeug [:ars/id :vehicle/vin])
                                    {:make/name "BMW" :vehicle/model "320i xDrive Limousine MX" :vehicle/engine-type :engine.type/internal-combustion})]
     [vehicle-details-header {:vehicle/vin "VSSZZZKNZNW005804" :make/name "Seat" :vehicle/model "TARRACO" :vehicle/license-plate "ND-PT364" :contract/name "Arval DeFleet"}]
     [vehicle-details-header {:vehicle/vin "WVGZZZA1ZLV140215" :make/name "Volkswagen" :vehicle/model "T-ROC"}]
     [vehicle-details-header {:vehicle/vin "WF0AXXWPMAHD75411" :make/name "Ford" :vehicle/model "KUGA" :vehicle/engine-type :engine.type/internal-combustion :vehicle/license-plate "RT-TZ106" :fahrzeug/labels #{:label/quick-turnaround}}]
     [vehicle-details-header {:vehicle/vin "W1K1771141J294758" :make/name "Mercedes Benz" :vehicle/model "A-KLASSE" :vehicle/engine-type :engine.type/internal-combustion}]
     [vehicle-details-header {:vehicle/vin "JMZND6E7600657733" :make/name "Kia" :vehicle/model "Sportage (NQ5) HEV/PHEV" :vehicle/engine-type :engine.type/hybrid :color/description "(1K) PEARL BLACK M" :vehicle/equipment ["Antenna" "Cappelliera" "Doppia chiave" "Kit gonfiaggio" "Libretto assistenza stradale" "Libretto garanzia" "Manuale uso e manutenzione" "Porta targa anteriore" "Tappetino Standard CJ141ADE00" "Tools"] :vehicle/weight 1557 :fahrzeug/labels #{:label/quick-turnaround :label/all-risk-insurance}}]

     [mobile/inline-prompt :scan (tr "Please scan the ARS-ID with the Hardware Scanner")]
     [mobile/table {:title "License Plate" :items [[:div.h-10]]}]]))

(defscene vehicle-details-header-minimal
  (let [fahrzeug {:vehicle/vin "U5YPU81BASL375939"}]
    [mobile/scroll-content
     [mobile/navbar {:title "Eingangsinspektion" :back [mobile/back-link]}]
     [vehicle-details-header fahrzeug]
     [mobile/table {:title "License Plate" :items [[:div.h-10]]}]]))

(defn transport-mode [mode]
  (case mode
    :mode/train (tr "Train")
    :mode/truck (tr "Truck")
    :mode/ship (tr "Ship")
    :mode/own-wheels (tr "Own wheels")
    nil))

(defn status-card [fahrzeug {:keys [phase active?]}]
  (let [entry? (= phase :entry)
        exit? (= phase :exit)
        [text-color bg-color icon] (cond
                                     (and entry? (:entry/datetime fahrzeug)) ["text-blue-700" "bg-blue-100" :check]
                                     (and exit? (:exit/datetime fahrzeug)) ["text-yellow-700" "bg-yellow-100" :check]
                                     :else ["text-slate-500" "bg-slate-100" :clock])]
    [:<>
     [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto.border.border-slate-300
      [:div.px-5.py-3.border-b.border-slate-300.flex.items-center.gap-2 {:class [bg-color text-color]}
       [ui/icon {:size 24} icon]
       [:h2.text-base.font-bold.leading-none.mt-0
        (if entry?
          (when-let [entered-at (:entry/datetime fahrzeug)]
            [:span {:class text-color}
             (str/capitalize (tr "Entered")) ": " (time-format/->str {:format :datetime} entered-at)])
          (if-let [exited-at (:exit/datetime fahrzeug)]
            [:span {:class text-color}
             (str/capitalize (tr "Exited")) ": " (time-format/->str {:format :datetime} exited-at)]
            [:span {:class text-color} (tr "not exited yet")]))]]
      (let [order (if entry? (:entry/transport-order fahrzeug) (:exit/transport-order fahrzeug))]
        [:div
         [:div.px-5.py-2.tracking-wider.uppercase.font-bold.text-xs.border-b.leading-none.bg-slate-50.shadow-inner
          (tr "Transport Order")]
         [:table.w-full
          [:tbody
           (when (get-in order [:transport/forwarder :address/name])
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr :transport/forwarder)]
              [:td.pr-5.py-2
               (get-in order [:transport/forwarder :address/name])]])
           (when (get-in order [:transport-order/contract :contract/name])
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr :transport-order/contract)]
              [:td.pr-5.py-2
               (get-in order [:transport-order/contract :contract/name])]])
           (when-let [entry-date (get fahrzeug
                                      (case phase
                                        :entry :entry/expected-datetime
                                        :exit :fahrzeug/expected-customer-delivery-datetime))]
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"}
               (case phase
                 :entry (tr :entry/expected-datetime)
                 :exit (tr :fahrzeug/expected-customer-delivery-datetime))]
              [:td.pr-5.py-2
               [:div.flex.items-center.gap-3
                (case phase
                  :entry
                  (time-format/->str {:format :datetime} entry-date)
                  :exit
                  (time-format/->str {:format :datetime} entry-date))
                (when (and (= :exit phase)
                           (contains? (set (:fahrzeug/priorities fahrzeug)) :prioritiy/binding-customer-appointment))
                  [:span.font-bold
                   "🤝 " (tr "Binding")])]]])
           (when (:exit/expected-datetime fahrzeug)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Expected departure")]
              [:td.pr-5.py-2 (time-format/->str {:format :date} (:exit/expected-datetime fahrzeug))]])

           (when (and (:fahrzeug/destination fahrzeug) exit?)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr :ductile.workflow/dispatchable?)]
              [:td.pr-5.py-2 {:data-testid "fahrzeug-dispatchable?"}
               (cond-> (tr (if (:ductile.workflow/dispatchable? fahrzeug) "Yes" "No"))
                 (:dispo/available-datetime fahrzeug)
                 (str ": " (time-format/->str {:format :datetime} (:dispo/available-datetime fahrzeug))))]])

           (when (:origin/location order)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Origin")]
              [:td.pr-5.py-2 (location (:origin/location order))]])

           (when (:destination/location order)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Destination")]
              [:td.pr-5.py-2 {:data-testid (str (name phase) "-transport-order-destination-location")} (location (:destination/location order))]])

           (when (and (:fahrzeug/destination fahrzeug)
                      (= :exit phase)
                      (not= (:fahrzeug/destination fahrzeug)
                            (:destination/location order)))
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Compound override destination")]
              [:td.pr-5.py-2 {:data-testid "fahrzeug-destination"} (location (:fahrzeug/destination fahrzeug))]])

           (when (:transport/name order)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Transport name")]
              [:td.pr-5.py-2 (:transport/name order)]])
           (when (:transport/mode order)
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Transport mode")]
              [:td.pr-5.py-2
               (transport-mode (:transport/mode order))]])
           (when (and exit? (:fahrzeug/exit-delivery-datetime fahrzeug))
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Delivery")]
              [:td.pr-5.py-2 (time-format/->str (:fahrzeug/exit-delivery-datetime fahrzeug))]])
           (when (and exit? (:fahrzeug/customs-number fahrzeug))
             [:tr.border-b.last:border-b-0
              [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Customs number")]
              [:td.pr-5.py-2 (:fahrzeug/customs-number fahrzeug)]])]]])
      (let [transport (case phase
                        :entry (:entry/transport fahrzeug)
                        :exit (:exit/transport fahrzeug))]
        [:div
         [:div.px-5.py-2.tracking-wider.uppercase.font-bold.text-xs.border-t.border-b.leading-none.bg-slate-50.shadow-inner
          (tr "Transport")]
         [:table.w-full
          [:tbody
           [:<>
            (when-let [tname (:transport/name transport)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Transport name")]
               [:td.pr-5.py-2 [ui/link {:href (router/path '[compound :compound-name transports :transport-id]
                                                           {:transport-id (:ductile/id transport)
                                                            :compound-name (:compound/name fahrzeug)})}
                               tname]
                (when (= :mode/ship (:transport/mode transport))
                  [ui/button {:variant :destructive-white
                              :size 1
                              :class "ml-4 float-right"
                              :data-testid "remove-from-load"
                              :on-click #(when (js/confirm (tr "Are you sure you want to remove this vehicle from the load?"))
                                           (deframe/call-lambda 'ductile.transport/remove-vehicle
                                                                {:ductile/id (:ductile/id fahrzeug)
                                                                 :phase phase
                                                                 :lambda/context :fahrzeug}))}
                   "Remove"])]])
            (when-let [tmode (transport-mode (or (if entry?
                                                   (:entry/mode fahrzeug)
                                                   (:exit/mode fahrzeug))
                                                 (:transport/mode transport)))]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Transport mode")]
               [:td.pr-5.py-2 tmode]])
            (when-let [truck-number (-> transport :transport/transporter :truck/number)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Truck number")]
               [:td.pr-5.py-2 truck-number]])
            (when-let [carrier (-> transport :transport/carrier)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Carrier")]
               [:td.pr-5.py-2 ((some-fn :address/name :company/name) carrier)]])
            (when-let [license-plate (:transport/license-plate transport)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "License plate")]
               [:td.pr-5.py-2 license-plate]])
            (when-let [lid (:load/id transport)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Load ID")]
               [:td.pr-5.py-2 [ui/link {:href (router/path '[compound :compound-name transports :transport-id]
                                                           {:transport-id (:ductile/id transport)
                                                            :compound-name (:compound/name fahrzeug)})}
                               lid]]])
            (when-let [ln (:transport/load-number transport)]
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Load #")]
               [:td.pr-5.py-2 [ui/link {:href (router/path '[compound :compound-name transports :transport-id]
                                                           {:transport-id (:ductile/id transport)
                                                            :compound-name (:compound/name fahrzeug)})}
                               ln]]])]]]])

      (when (and entry? (:parking-spot/zone fahrzeug))
        (let [exited? (:exit/datetime fahrzeug)]
          [:div
           [:div.px-5.py-2.tracking-wider.uppercase.font-bold.text-xs.border-t.border-b.leading-none.bg-slate-50.shadow-inner
            (tr "Compound")]
           [:table.w-full
            [:tbody
             [:<>
              [:tr.border-b.last:border-b-0
               [:th.pl-5.pr-3.py-2.text-slate-600.font-normal.text-left {:class "w-[190px]"} (tr "Parked at") " "]
               [:td.pr-5.py-2
                [:span {:class (when exited? "line-through text-slate-500")}
                 (parking-utils/parking-spot->str fahrzeug)]
                (when exited?
                  [:span.ml-2 (tr "Exited")])]]]]]]))]]))

(defn confirm-cancelling-service [fahrzeug service]
  (let [close! #(deframe/route-dissoc! ::confirm-cancelling-service)]
    [ui/alert {:open? true :on-close close!}
     [ui/alert-content
      [ui/modal-close {:on-click close!}]
      [ui/alert-title
       (tr "Cancel service")]
      [ui/alert-description
       (tr "Are you sure you want to cancel \"%1\"?" (:service/code service))]
      [:div {:class "flex flex-col mt-4 gap-3"}
       [ui/alert-action
        [ui/button
         {:variant :destructive
          :size 3
          :on-click (fn []
                      (deframe/route-dissoc! ::confirm-cancelling-service)
                      (deframe/call-lambda
                       'ductile.workflow/cancel-service
                       {:ductile/id (:ductile/id fahrzeug)
                        :service/code (:service/code service)}))}
         (tr "Yes, cancel service")]]
       [ui/alert-cancel
        [ui/button
         {:size 3 :on-click close!}
         (tr "Don't cancel the service")]]]]]))

(defn service-list-item [fahrzeug {:as service :keys [condition error extra?]}]
  (let [completed-at (:service/completed-at service)]
    [:div.border-b.px-5.py-3.last:border-b-0
     [:div.flex.items-center.gap-2.justify-between
      [:div.flex-auto.flex.items-center.gap-2
       [:div.text-sm
        #_{:class (when (contains? #{:optionality/must :optionality/should} (:service/optionality service)) "font-bold")}
        (ui/service-label service)]
       [ui/optionality-badge (:service/optionality service)]]
      [:div.flex.items-center.mr-1 {:class "text-[12px]"}
       (when-not completed-at
         [ui/link
          {:as :button
           :variant :primary
           :on-click #(ui/show-form-modal! 'ductile.workflow/service-complete-form
                                           {:ductile/id (:ductile/id fahrzeug)
                                            :service/code (:service/code service)})}
          (tr "Mark completed")])]
      (when (or (= :optionality/must (:service/optionality service))
                (:service/schedule service))
        [:div.flex.items-center {:class "text-[12px]"}
         [ui/link
          {:as :button
           :variant :destructive
           :on-click #(deframe/route-assoc! ::confirm-cancelling-service service)}
          (tr "Mark cancelled")]])]
     [:div.text-xs
      (when condition [:div condition])
      (when-let [scheduled-for (:service/scheduled-for-datetime service)]
        [:div (time-format/->str {:format :datetime} scheduled-for)])]]))

(defn service-group-list-item [services]
  (r/with-let [!expanded? (r/atom false)]
    [:div.border-b.last:border-b-0
     [:div.flex.gap-2.px-5.py-3.hover:text-indigo-600.group.cursor-pointer.items-center
      {:on-click #(swap! !expanded? not)
       :class (when-not @!expanded? "hover:bg-indigo-50")}
      [:div.flex.gap-2
       [ui/icon {:size 20 :class (str "mt-[4px] transition " (when @!expanded? "rotate-90"))} :chevron-right]]
      [:div.flex.items-center.gap-2.flex-auto
       [:div.text-sm
        (:service/code (last services))]]
      [ui/badge {:variant :gray} (str (count services) "×")]]
     (when @!expanded?
       (into [:div.ml-10.mr-4.mb-3.flex.flex-col.gap-2]
             (map (fn [{:as service :keys [service/result]}]
                    [:div.text-xs.bg-slate-100.rounded.px-3.py-2.flex.flex-col.gap-2
                     (when (ui/debug-mode?) (pr-str (:ductile/id service)))
                     (when-let [scheduled-for-datetime (:service/scheduled-for-datetime service)]
                       [:div.flex.items-center.gap-2
                        [ui/icon :clock]
                        (str (tr "Scheduled for") " "
                             (time-format/->str {:format :datetime} scheduled-for-datetime))])
                     [:div.flex.items-center.gap-2
                      [ui/icon-socket [ui/icon {:size 16} :check]]
                      (str (tr "Completed at") " "
                           (time-format/->str {:format :datetime} (:service/completed-at service)))]
                     (when-let [details (service-details service)]
                       [:div.flex.items-center.gap-2
                        [ui/icon-socket [ui/icon {:size 16} :file-text]]
                        details])
                     [ui/service-notifications service]
                     (when result
                       [render/inspect (viewer/table (cond-> result
                                                       (not (sequential? result)) vector
                                                       true table-stats/normalize-table-data))])
                     #_[:span (:ductile/id service)]
                     (when (= :billing.mode/not-invoiced (:billing/mode service))
                       [:span.text-gray-400.flex.items-center.gap-2 (tr "will not be charged")])
                     (when-let [{:invoice/keys [invoice-number billing-date]
                                 :billing/keys [currency]} (:billing/invoice service)]
                       (let [{:billing/keys [amount]} service]
                         [:a.text-gray-400.flex.items-center.gap-2 {:href (str "/invoice/" invoice-number)}
                          [:span invoice-number]
                          [:span (time-format/->str {:format :date} billing-date)]
                          [:span (util/format-amount amount currency)]]))]))
             services))]))

(defn completed-services-card [fahrzeug]
  [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
   [:div.px-5.py-2.bg-slate-100.border-b
    [:div.leading-none.justify-between.flex.items-center.gap-4
     [:h2.text-base.font-bold (tr "Completed services")]]]
   (into [:div]
         (map (fn [[_service-code services]]
                [service-group-list-item services]))
         (group-by :service/code (:service.event/completed fahrzeug)))])


(defn active-services-card [fahrzeug]
  [:<>
   (when @(deframe/!route-get-in [:fetch :result ::services-modal-open?])
     [ui/modal {:on-close #(do (deframe/route-dissoc-in! [:fetch :result ::services-modal-open?])
                               (deframe/route-dissoc-in! [:fetch :error]))}
      [new-service-form fahrzeug]])
   [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
    [:div.px-5.py-2.bg-slate-100.border-b
     [:div.leading-none.justify-between.flex.items-center.gap-4
      [:h2.text-base.font-bold (tr "Active Services")]
      [ui/button
       {:variant :white
        :size 2
        :on-click #(deframe/route-assoc-in! [:fetch :result ::services-modal-open?] true)}
       (tr "Assign service")]]]
    (if-let [services (not-empty (:workflow/active-services fahrzeug))]
      (let [!can-do-services-visible? (deframe/!route-get ::can-do-services-visible? false)
            optional->services (group-by (comp boolean #{:optionality/can} :service/optionality) services)]
        [:div
         (into [:div]
               (map (fn [service] [service-list-item fahrzeug service]))
               (cond-> (optional->services false)
                 @!can-do-services-visible?
                 (concat (optional->services true))))
         (when (seq (optional->services true))
           [:div.border-b.px-5.py-3.last:border-b-0.text-sm.text-indigo-600.border-t.cursor-pointer.hover:bg-indigo-50
            {:on-click #(deframe/route-assoc! ::can-do-services-visible? (not @!can-do-services-visible?))
             :data-testid "can-do-services-toggle"}
            (str (if @!can-do-services-visible?
                   (tr "Show less")
                   (tr "Show more"))
                 "…")])])
      [:div.px-5.py-2 (tr "No services assigned.")])]])

(defn service-step [service]
  [:div
   {:class [(when-not (or (:service/active? service)
                          (:service/scheduled-datetimes service)) "opacity-50")]}
   [:div.flex.items-center.gap-2.text-sm
    [:span.font-bold (ui/service-label service)]
    [ui/optionality-badge (:service/optionality service)]
    (when (:service/active? service)
      [ui/badge {:size 1 :variant :blue} (tr "Active")])]
   (when-let [scheduled (seq (:service/scheduled-datetimes service))]
     [:div.flex.gap-1.mt-1
      [:div.font-bold.text-orange-600.flex.gap-1
       {:class ["text-[12px]"]}
       [ui/icon {:size 12 :style {:margin-top 3}} :clock]
       (tr "Scheduled for")]
      (into [:div]
            (map (fn [dt]
                   [:div.text-orange-600
                    {:class "text-[12px]"}
                    ;; TODO: this should be a js date already for ->str to work
                    (time-format/->str (js/Date. dt))]))
            scheduled)])])

(defn workflow-card [fahrzeug]
  [:<>
   [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
    [:div.px-5.py-2.bg-slate-100.border-b
     [:div.leading-none.justify-between.flex.items-center.gap-4
      [:h2.text-base.font-bold (tr "Workflow Plan")]
      #_[ui/button {:variant :white
                    :size 3
                    :href (router/path '[compound :compound-name vehicle :fahrzeug-id workflow-history]
                                       {:fahrzeug-id (:vehicle/vin fahrzeug)
                                        :compound-name (:compound/name fahrzeug)})} (tr "History")]]]

    (into [:div.flex.flex-col]
          (map (fn [service]
                 [:div.px-5.py-3.border-b.border-slate-300.last:border-b-0
                  [service-step service]]))
          (:workflow/scheduled-services fahrzeug))]])

(defn events-card [events]
  [:div.bg-white.rounded-lg.shadow-lg.overflow-hidden
   [:div.px-5.py-2.bg-slate-50.border-b.items-center.flex {:class "min-h-[62px]"}
    [:div.leading-none.justify-between.flex.items-center.gap-4
     [:h2.text-base.font-bold (tr "Recent events")]]]
   (if (seq events)
     [:div
      [ui/events-list events]]
     [:div.px-5.py-2 (tr "No events recorded yet.")])])

(defn edi-json [_idx {:edi_instructions/keys [id created_at direction message_type file-name instruction_raw] :as message}]
  (r/with-let [!mode (r/atom :normalized)
               !expanded? (r/atom false)]
    [:div.relative.mb-4
     [:span.absolute.rounded-full.bg-slate-300
      {:class "w-[2px] left-[27px] top-[28px] -bottom-[30px]"}]
     [:span.block.absolute.w-2.h-2.bg-slate-300.rounded-full
      {:class "left-[24px] top-[16px]"}]
     [:div.pl-11.pr-4.py-2.relative
      [:div.flex.items-center.justify-between.gap-2.text-sm
       [:span.flex.gap-2
        [:span.font-bold
         (if (= "incoming" direction)
           (tr "Incoming")
           (tr "Outgoing"))]
        [:span (time-format/->str created_at)]]
       [:div.flex.items-center
        {:class "text-[11px]"}
        [:button.rounded-l.px-2.border.border-r-0
         {:class (if (= @!mode :normalized) "border-indigo-600 bg-indigo-600 text-white " "bg-white border-slate-400 ")
          :on-click #(reset! !mode :normalized)} "Normalized"]
        [:button.rounded-r.px-2.border
         {:class (if (= @!mode :raw) "border-indigo-600 bg-indigo-600 text-white " "bg-white border-slate-400 ")
          :on-click #(reset! !mode :raw)} "Raw"]]]]
     [:div.mr-4.bg-white.rounded-md.border.border-slate-300.shadow-inner.overflow-x-auto
      {:class "ml-[48px]"}
      (if (= @!mode :raw)
        [:table.w-full.text-xs
         [:tbody
          [:tr.border-b.last:border-b-0
           [:th.px-3.py-2.text-slate-600.text-left.whitespace-nowrap.align-top (tr "Raw")]
           [:td.px-3.py-2 instruction_raw]]]]
        [:table.w-full.text-xs
         [:tbody
          (when id
            [:tr.border-b.last:border-b-0
             [:th.px-3.pr-3.py-2.text-slate-600.text-left.whitespace-nowrap "Id"]
             [:td.px-3.py-2 id]])
          [:tr.border-b.last:border-b-0
           [:th.px-3.pr-3.py-2.text-slate-600.text-left.whitespace-nowrap (tr "Type")]
           [:td.px-3.py-2 message_type]]
          [:tr.border-b.last:border-b-0
           [:th.px-3.py-2.text-slate-600.text-left.whitespace-nowrap (tr "Created at")]
           [:td.px-3.py-2 (time-format/->str created_at)]]
          [:tr.border-b.last:border-b-0
           [:th.px-3.pr-3.py-2.text-slate-600.text-left.whitespace-nowrap (tr "Direction")]
           [:td.px-3.py-2 direction]]
          [:tr
           {:class (when @!expanded? "border-b")}
           [:td.relative.text-indigo-600.px-3.py-1.hover:bg-indigo-50.py-1.cursor-pointer
            {:on-click #(swap! !expanded? not)
             :col-span 2
             :class (str "text-[11px] " (when @!expanded? "bg-indigo-50 hover:bg-indigo-100"))}
            (if @!expanded? (tr "Show less") (tr "Show all fields"))]]]
         (when @!expanded?
           (into [:<>
                  (when file-name
                    [:tr.border-b.last:border-b-0
                     [:th.px-3.pr-3.py-2.text-slate-600.text-left.whitespace-nowrap (tr "File name")]
                     [:td.px-3.py-2 file-name]])]
                 (map (fn [[k v]]
                        (when v [:tr.border-b.last:border-b-0
                                 [:th.px-3.py-2.text-slate-600.text-left.whitespace-nowrap k]
                                 [:td.px-3.py-2 (nextjournal.clerk.render/inspect v)]])))
                 (sort-by first (:edi_instructions/instruction_json message))))])]]))

(defn edi-card [edi-messages]
  (r/with-let [!direction (r/atom #{"incoming" "outgoing"})]
    (let [filtered-edi-messages (filter #(contains? @!direction (:edi_instructions/direction %))
                                        edi-messages)]
      [:div.bg-slate-50.rounded-lg.shadow-lg.overflow-hidden
       [:div.px-5.py-2.bg-slate-100.border-b.border-slate-300.items-center.flex {:class "min-h-[62px]"}
        [:div.leading-none.justify-between.flex.items-center.gap-4.w-full
         [:h2.text-base.font-bold (tr "EDI messages")]
         [:div.flex.items-center.text-xs
          [:button.rounded-l.px-2.border.border-r-0.py-1
           {:class (if (= @!direction #{"incoming" "outgoing"}) "border-indigo-600 bg-indigo-600 text-white " "bg-white border-slate-400 ")
            :on-click #(reset! !direction #{"incoming" "outgoing"})} (tr "Both")]
          [:button.px-2.border.border-r-0.py-1
           {:class (if (= @!direction #{"incoming"}) "border-indigo-600 bg-indigo-600 text-white " "bg-white border-slate-400 ")
            :on-click #(reset! !direction #{"incoming"})} (tr "Incoming")]
          [:button.rounded-r.px-2.border.py-1
           {:class (if (= @!direction #{"outgoing"}) "border-indigo-600 bg-indigo-600 text-white " "bg-white border-slate-400 ")
            :on-click #(reset! !direction #{"outgoing"})} (tr "Outgoing")]]]]
       (if (seq filtered-edi-messages)
         (into [:div] (map-indexed (fn [i msg] [edi-json i msg]) (sort-by :edi_instructions/created_at filtered-edi-messages)))
         [:div.px-5.py-2 (tr "No EDI messages.")])])))

(defn invoices-card [invoices]
  [:div.bg-slate-50.rounded-lg.shadow-lg.overflow-hidden
   [:div.px-5.py-2.bg-slate-100.border-b.border-slate-300.items-center.flex {:class "min-h-[62px]"}
    [:div.leading-none.justify-between.flex.items-center.gap-4
     [:h2.text-base.font-bold (tr "Invoices")]]]
   (if (some-> invoices :invoices :rows seq)
     [render/inspect (viewer/table (-> (:invoices invoices)
                                       (update :head #(take 5 %))
                                       (update :rows (fn [rows]
                                                       (map #(take 5 %) rows)))))]
     [:div.px-5.py-2 (tr "No invoices found")])])


(defn storage-billing-card [{:keys [storage-billings]}]
  [:div.bg-slate-50.rounded-lg.shadow-lg.overflow-hidden
   [:div.px-5.py-2.bg-slate-100.border-b.border-slate-300.items-center.flex {:class "min-h-[62px]"}
    [:div.leading-none.justify-between.flex.items-center.gap-4
     [:h2.text-base.font-bold (tr "Storage Invoice")]]]
   (if (some-> storage-billings :rows seq)
     [render/inspect (viewer/table storage-billings)]
     [:div.px-5.py-2 (tr "No storage invoice found")])])

(defn image-modal [{:as img :keys [label url on-close]}]
  [ui/modal (assoc img :attrs {:class "max-h-[80vh] w-[320px] md:w-[500px] lg:w-[800px]"})
   [:div.bg-white.rounded-lg.shadow-lg
    [:div.flex.justify-between.items-center.py-4.px-6.border-b
     [:h4.mt-0.text-base.font-bold (or label (tr "Image Detail"))]]
    [:div.bg-slate-50.py-4.px-6
     [:img {:src url}]]]])

(defn image-tile [{:as img :keys [label remove-fn url]}]
  (r/with-let [!modal-open? (r/atom false)]
    [:<>
     [:div.relative
      [:div.rounded-md.border.border-slate-400.hover:border-indigo-600.shadow.bg-cover.cursor-pointer
       {:class "w-[80px] h-[80px]"
        :style {:background-image (str "url(" url ")") :background-color "#eee"}
        :on-click #(reset! !modal-open? true)}]
      (when label
        [:div {:class "w-[80px] text-[10px]"} label])
      (when remove-fn
        [:div.bg-indigo-600.border-2.border-white.rounded-full.text-white.flex.items-center.justify-center.absolute.cursor-pointer.hover:bg-indigo-700
         {:class "w-[24px] h-[24px] -top-[7px] -right-[7px]"}
         [ui/icon {:size 14} :x]])]
     (when @!modal-open?
       [image-modal (assoc img :on-close #(reset! !modal-open? false))])]))

(def photo-attrs #{:photo/cmr
                   :photo/damages
                   :photo/details
                   :photo/vehicle-front
                   :photo/vehicle-back
                   :photo/vehicle-right
                   :photo/vehicle-left
                   :photo/vehicle-vin})

(defn services-with-photo-comments [{:fahrzeug/keys [services]}]
  (filter (fn [service]
            (contains? (set (map :ductile.comment/kind (:ductile/comments service)))
                       :ductile.comment.kind/image))
          services))

(defn services-with-photo-data [{:fahrzeug/keys [services]}]
  (filter (fn [service]
            (not-empty (set/intersection photo-attrs (set (keys service)))))
          services))

(defn finite-coordinate? [coord]
  (and (number? coord)
       (js/Number.isFinite coord)))

(defn geocoded-location-event? [event]
  (and (finite-coordinate? (:location/latitude event))
       (finite-coordinate? (:location/longitude event))))

(defn images-card [fahrzeug]
  (when-let [images (not-empty (concat (mapcat :ductile/comments (services-with-photo-comments fahrzeug))
                                       (mapcat (fn [s] (flatten (vals (select-keys s photo-attrs))))
                                               (services-with-photo-data fahrzeug))))]
    [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
     [:div.px-5.py-2.bg-slate-100.border-b
      [:div.leading-none.justify-between.flex.items-center.gap-4
       [:h2.text-base.font-bold (tr "Images")]]]
     (into [:div.flex.flex-wrap.gap-3.px-6.py-4]
           (map (fn [img]
                  [image-tile {:url (str "/cas/" (:cas/multihash img))}])
                images))]))

(defn workflow-entity-locations-map [scans !the-map !selected-scan-id]
  (let [!map-ref (hooks/use-ref nil)
        !satellite? (hooks/use-state true)
        satellite-style "mapbox://styles/mapbox/satellite-streets-v12"
        default-style "mapbox://styles/ductile/clxbm7c55029q01pn72ehh68d"
        features (hooks/use-memo
                  (fn []
                    (->> scans
                         (filter geocoded-location-event?)
                         (map (fn [s]
                                (let [id (str (:ductile/id s))]
                                  (j/lit {:type "Feature"
                                          :geometry {:type "Point"
                                                     :coordinates [(:location/longitude s) (:location/latitude s)]}
                                          :properties {"id" id}}))))))
                  [scans])
        feature-collection (hooks/use-memo
                            #(turf/featureCollection (clj->js features))
                            [features])]
    (mapbox/use-map !map-ref {:style (if @!satellite? satellite-style default-style)
                              :on-load (fn [m] (reset! !the-map m))})
    (hooks/use-effect (fn []
                        (when-let [^js m @!the-map]
                          (.setStyle m (if @!satellite? satellite-style default-style))))
                      [@!satellite?])
    (hooks/use-effect (fn []
                        (when-let [^js m @!the-map]
                          (let [on-style-load (fn []
                                                (let [source-id "scans"]
                                                  (if-let [^js source (.getSource m source-id)]
                                                    (.setData source feature-collection)
                                                    (.addSource m source-id (j/lit {:type "geojson" :data feature-collection})))
                                                  (when-not (.getLayer m "scans-circle")
                                                    (.addLayer m (clj->js {:id "scans-circle"
                                                                           :type "circle"
                                                                           :source source-id
                                                                           :paint {:circle-radius 6
                                                                                   :circle-color "#4f46e5"
                                                                                   :circle-stroke-width 2
                                                                                   :circle-stroke-color "#ffffff"}})))
                                                  (when-not (.getLayer m "selected-scan")
                                                    (.addLayer m (clj->js {:id "selected-scan"
                                                                           :type "circle"
                                                                           :source source-id
                                                                           :paint {:circle-radius 10
                                                                                   :circle-color "#ef4444"
                                                                                   :circle-stroke-width 3
                                                                                   :circle-stroke-color "#ffffff"}
                                                                           :filter ["==" ["get" "id"] (or @!selected-scan-id "none")]})))))]
                            (.on m "style.load" on-style-load)
                            (on-style-load)
                            (when (seq features)
                              (let [fit-bounds! (fn []
                                                  (let [container (.getContainer m)
                                                        width (.-clientWidth container)
                                                        height (.-clientHeight container)]
                                                    (when (and (pos? width) (pos? height))
                                                      (.resize m)
                                                      (.fitBounds m (turf/bbox feature-collection) #js {:padding 40 :maxZoom 15}))))]
                                (fit-bounds!)
                                (js/requestAnimationFrame fit-bounds!)))
                            (fn []
                              (.off m "style.load" on-style-load)))))
                      [@!the-map feature-collection @!satellite?])
    (hooks/use-effect (fn []
                        (when-let [^js m @!the-map]
                          (when (.getLayer m "selected-scan")
                            (.setFilter m "selected-scan" (clj->js ["==" ["get" "id"] (or @!selected-scan-id "none")])))))
                      [@!the-map @!selected-scan-id])
    [:div.relative
     [:div.bg-slate-300.rounded-md.border.border-slate-300.shadow.overflow-hidden
      {:ref !map-ref
       :style {:height "400px"}}]
     [:div.absolute.top-2.right-2
      [ui/button {:size 1
                  :variant :secondary
                  :on-click #(swap! !satellite? not)}
       (if @!satellite? (tr "Standard view") (tr "Satellite view"))]]]))

(defn workflow-entity-locations [location-events]
  (let [!the-map (hooks/use-state nil)
        !expanded? (hooks/use-state false)
        !selected-scan-id (hooks/use-state nil)]
    (if (seq location-events)
      [:div.p-5.flex.flex-col.gap-3
       [workflow-entity-locations-map location-events !the-map !selected-scan-id]
       [:div
        [:div.flex.items-center.gap-2.cursor-pointer.text-sm.text-slate-600.hover:text-indigo-600.transition
         {:on-click #(swap! !expanded? not)}
         [ui/icon {:class (when @!expanded? "rotate-90") :size 16} :chevron-right]
         (if @!expanded? (tr "Hide details") (tr "Show details"))]
        (when @!expanded?
          [:div.mt-3.overflow-x-auto.border.rounded
           [:table.w-full.text-sm
            [:thead.bg-slate-50.border-b
             [:tr
              [:th.px-4.py-2.text-left.font-semibold.text-slate-700 (tr "Timestamp")]
              [:th.px-4.py-2.text-left.font-semibold.text-slate-700 (tr "Type")]
              [:th.px-4.py-2.text-left.font-semibold.text-slate-700 (tr "Coordinates")]]]
            [:tbody
             (map (fn [{:as e :event/keys [timestamp title] :location/keys [longitude latitude]}]
                    (let [id (str (:ductile/id e))
                          selected? (= id @!selected-scan-id)]
                      [:tr.border-b.last:border-b-0.hover:bg-slate-50.cursor-pointer
                       {:class (when selected? "bg-red-50")
                        :on-click (fn []
                                    (reset! !selected-scan-id id)
                                    (when-let [^js m @!the-map]
                                      (.flyTo m (clj->js {:center [longitude latitude]
                                                          :zoom 18}))))}
                       [:td.px-4.py-2.tabular-nums (time-format/->str {:format :datetime} timestamp)]
                       [:td.px-4.py-2.tabular-nums title]
                       [:td.px-4.py-2.tabular-nums (str latitude ", " longitude)]]))
                  location-events)]]])]]
      [:div.px-5.py-2 (tr "No GPS positions recorded.")])))

(defn show-fahrzeug [fahrzeug]
  [:<>
   [:div
    [:div.lg:grid.grid-cols-1.gap-5
     [:div.rounded-lg.shadow-lg.overflow-hidden
      [vehicle-details-header {:editable? true} fahrzeug]]
     [ui/error-view (:error @deframe/!current-route-fetch)]]
    (when-let [{:fahrzeug/keys [prev next]} (not-empty (select-keys fahrzeug [:fahrzeug/prev :fahrzeug/next]))]
      [:div.flex.items-center.justify-between.border-b.py-2.text-slate-600
       [:div (when prev
               [:a.flex.items-center.gap-1 {:href (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                                               {:fahrzeug-id (:ductile/id prev)
                                                                :compound-name (-> prev :ductile/location :compound/name)})}
                [ui/icon :chevron-left] (tr "Predecessor")])]
       [:div (when next
               [:a.flex.items-center.gap-1 {:href (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                                               {:fahrzeug-id (:ductile/id next)
                                                                :compound-name (-> next :ductile/location :compound/name)})}
                (tr "Successor") [ui/icon :chevron-right]])]])
    (when (ui/debug-mode?)
      [:div.mt-5
       [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
        [:div.px-5.py-2.bg-slate-100.border-b
         (render/inspect (viewer/with-viewers
                           (into [] (remove #{dcv/blocks-viewer}) (viewer/get-default-viewers))
                           fahrzeug))]]])
    [:div.flex.flex-col.lg:grid.grid-cols-2.gap-5.pt-5
     [:div
      [status-card fahrzeug {:phase :entry :active? (and (:entry/datetime fahrzeug) (not (:exit/datetime fahrzeug)))}]]
     [:div
      [status-card fahrzeug {:phase :exit :active? (:exit/datetime fahrzeug)}]]]
    [:div.flex.flex-col.lg:grid.grid-cols-2.gap-5.pt-5
     [:div
      [active-services-card fahrzeug]
      (when (seq (:workflow/scheduled-services fahrzeug))
        [:div.mt-5 [workflow-card fahrzeug]])
      (when (not-empty (:service.event/completed fahrzeug))
        [:div.mt-5
         [completed-services-card fahrzeug]])
      [:div.mt-5
       [ui/loading-promise-view events-card (http/$lambda 'ductile.workflow.engine/events-for-fahrzeug (:path-params @deframe/!current-route))]]
      (when-current-user-scope
       :scope/billing
       [:div.mt-5
        [ui/loading-promise-view invoices-card (http/$lambda 'ductile.invoices/$list {:fahrzeug-id (:ductile/id fahrzeug)})]])
      (when-current-user-scope
       :scope/billing
       [:div.mt-5
        [ui/loading-promise-view storage-billing-card (http/$lambda 'ductile.billing/$fahrzeug-storage-billings {:ductile/id (:ductile/id fahrzeug)})]])
      [:div.mt-5
       (when fahrzeug
         [ui/loading-promise-view edi-card (http/$edi-vin-search (:vehicle/vin fahrzeug) (:ductile/id fahrzeug))])]]
     [:div
      [:div.mt-5 [images-card fahrzeug]]]]]
   (when-let [service @(deframe/!route-get ::confirm-cancelling-service)]
     [confirm-cancelling-service fahrzeug service])])

(defn location-view [location]
  [:div
   (when (:location/types location)
     [:span.relative
      {:class ["mr-1" "top-[-1px]"]}
      [ui/badge {:variant :white-blue :size 1}
       (str/join ", " (map tr (:location/types location)))]])
   [:span.font-bold (:address/name location)]
   (str ", "
        (:address/street location) ", "
        (:address/postcode location) " " (:address/city location) ", "
        (:country/code (:address/country location)))])

(defn parking-spot? [v]
  (and (map? v) (some #(contains? v %) [:parking-spot/row :parking-spot/zone :parking-spot/place])))

(defn parking-spot-view [{:parking-spot/keys [zone row place]}]
  (str/join "/" (remove nil? [(or (:parking-zone/name zone) zone) row place])))

(defn transporter? [v]
  (and (map? v) (some #(contains? v %) [:transport/carrier :truck/number])))

(declare entity-view)

(defn transporter-view [v]
  [entity-view {:exclude [:ductile/id] :ui/layout :layout/nested} v])

(def event-icon-specs
  {:fahrzeug/parked {:icon/name :parking :icon/background "bg-blue-500"}
   :fahrzeug/entered {:icon/name :enter :icon/background "bg-orange-500"}
   :fahrzeug/exited {:icon/name :exit :icon/background "bg-green-500"}
   :service.event/invoiced {:icon/name :document :icon/background "bg-sky-500"}
   :service.event/completed {:icon/name :checkbox :icon/background "bg-blue-500"}
   :service.event/ordered {:icon/name :box :icon/background "bg-red-500"}
   :service.event/canceled {:icon/name :circle-backslash :icon/background "bg-gray-500"}
   :service.event/undone {:icon/name :reset :icon/background "bg-gray-500"}
   :service.event/edited {:icon/name :edit-3 :icon/background "bg-gray-500"}
   :dispo/withdrawal {:icon/name :upload :icon/background "bg-emerald-500"}
   :dispo/delivered {:icon/name :check :icon/background "bg-indigo-500"}})

(defn event-view [{:as event :event/keys [target kind]}]
  [:div.flex.gap-2
   (when-let [icon (get event-icon-specs (:event/kind event))]
     [:div.w-4.h-4.flex.flex-shrink-0.items-center.justify-center.rounded-full.text-white
      {:class (:icon/background icon)}
      [ui/icon {:size 12} (:icon/name icon)]])
   [:div
    [ui/event-summary {:span-block? false} event]
    (when-let [children (->> [(when-current-user-scope
                               :scope/service-event-user
                               (-> event :ductile/user :user/email))
                              (when-let [edi-provider (-> event :edi/provider)]
                                [:span (tr :edi/provider) ": " edi-provider])
                              (when (:event/undoable? event)
                                [:span.text-red-600.hover:underline.cursor-pointer
                                 {:on-click #(deframe/call-lambda 'ductile.workflow/undo-event event)}
                                 (tr "Undo event")])
                              (when (:event/editable? event)
                                [:span.text-indigo-600.hover:underline.cursor-pointer
                                 {:on-click #(ui/show-form-modal! 'ductile.workflow/edit-event-form event)
                                  :data-testid (str "edit-" (str/lower-case (:service/code event)))}
                                 (tr "Edit")])]
                             (remove nil?)
                             not-empty)]
      (into [:div.flex.gap-2.items-center
             {:class ["text-[11px]" "text-gray-500"]}]
            children))]])

(defn service-view [opts service]
  (r/with-let [id (str (:service/code service) "-" (random-uuid))]
    (let [expanded? @(deframe/!route-get-in [id :expanded?] (:ui/expanded? service))]
      [:div
       [:div.flex.items-center.justify-between
        [:div.flex.items-center.gap-2
         [:div.text-indigo-600.cursor-pointer.transition-all
          {:class (when expanded? ["rotate-90"])
           :on {:click [::deframe/route-update-in [id :expanded?] not]}
           :on-click #(deframe/route-update-in! [id :expanded?] not)}
          [ui/icon {:size 14} :chevron-right]]
         (if (:service/completed-at service)
           [ui/icon {:size 14} :checkbox]
           [ui/icon {:size 14} :box])
         [:div.font-bold (ui/service-label service)]
         (when (:service/schedule service)
           [ui/icon {:size 12} :clock])
         (when (:service/optionality service)
           [ui/optionality-badge (:service/optionality service)])
         (when (and (:service/completed-at service) (not expanded?))
           [:div.text-gray-500
            (str (tr :service/completed-at) " " (time-format/->str (:service/completed-at service)))])]
        [:div.flex.item.center.gap-2
         {:class "text-[12px]"}
         (when (:service/active? service)
           [ui/link
            {:as :button
             :variant :primary
             :on-click #(ui/show-form-modal! 'ductile.workflow/service-complete-form {:service/code (:service/code service)
                                                                                      :ductile/id (-> opts :context :ductile/id)})}
            [ui/icon {:size 12} :checkbox]
            (or (some-> (:service/confirm-label service)
                        tr)
                (tr "Mark completed"))])
         (when (and (:service/active? service)
                    (= :optionality/must (:service/optionality service))
                    (not (:service/completed-at service)))
           [ui/link
            {:as :button
             :variant :destructive
             :on-click #(deframe/route-assoc! ::confirm-cancelling-service service)}
            (tr "Mark cancelled")])]]
       (when expanded?
         [:div.text-gray-500.bg-slate-50.mt-1.ml-5.-mb-1.-mr-2
          [entity-view (merge opts {:nested? true
                                    :pull/pattern [:ductile/created-at
                                                   :service/completed-at
                                                   :event/undone-at
                                                   :ductile/file-attachment
                                                   :ductile/note
                                                   :billing/invoice
                                                   :*]
                                    :exclude (cond-> [:ductile/id
                                                      :scan/id
                                                      :service/code
                                                      :service/template
                                                      :ui/view
                                                      :ui/expanded?
                                                      :service/default-attrs
                                                      :service/required-scope
                                                      :target/events
                                                      :service/active?
                                                      :service/ductile-data-attrs
                                                      :ui/fields]
                                               (not (ui.util/has-required-scope? :scope/service-event-user)) (conj :ductile/user)
                                               (not (ui/debug-mode?)) (conj :service/template))}) service]])])
    (finally
      (deframe/route-dissoc! id))))

(defn document-view [document]
  [ui/link {:href (routes/path '[compound :compound-name bill-of-lading :ductile-id]
                               {:ductile-id (:ductile/id document)
                                :compound-name @deframe/!current-compound})}
   [ui/icon {:width 12 :height 12} :file]
   (:document/number document)])

(defn invoice-view [invoice]
  (let [{:invoice/keys [invoice-number billing-date]
         :billing/keys [currency]} invoice]
    [ui/link {:href (str "/invoice/" invoice-number)}
     [ui/icon {:width 12 :height 12} :file]
     [:span invoice-number]
     [:span (time-format/->str {:format :date} billing-date)]]))

(defn value-with-unit-view [[v unit]]
  (str v " " (tr unit)))

(defn recorded-form? [v]
  (and (map? v) (contains? v :form)))

(defn date-time-string? [s]
  (boolean (re-matches #"\d{4}-\d{2}-\d{2}T\d{2}:\d{2}" s)))

(defn blank-slate [& content]
  (into [:span.text-gray-500] content))

(defn image-view [v]
  (let [image-path (routes/cas-path nil v)
        state-path [(:cas/multihash v) :modal-open?]]
    [:<>
     [:div.rounded-md.overflow-hidden.border.border-gray-300.shadow.relative.hover:border-indigo-600.hover:outline-indigo-300.hover:outline-2.cursor-pointer
      {:class ["w-[200px]" "h-[100px]"]
       :on-click #(deframe/route-assoc-in! state-path true)}
      [:img.border.border-white.rounded-md.w-full.h-full.object-cover
       {:src image-path}]
      (when (:ductile/timestamp v)
        [:div.border.border-white.border-opacity-80.bg-black.bg-opacity-60.rounded.text-white.absolute.leading-none
         {:class ["right-[3px]" "bottom-[3px]" "px-[4px]" "py-[3px]" "text-[9px]"]}
         (time-format/->str (:ductile/timestamp v))])]
     (when @(deframe/!route-get-in state-path)
       [image-modal {:url image-path
                     :label (when (:ductile/timestamp v)
                              (time-format/->str (:ductile/timestamp v)))
                     :on-close #(deframe/route-dissoc-in! state-path)}])]))

(defn image? [v]
  (and (:cas/multihash v)
       (or (= (:ductile.comment/kind v) :ductile.comment.kind/image)
           (some-> (:file/mime-type v) (str/starts-with? "image/")))))

(defn file? [v]
  (and (:cas/multihash v)
       (or (:file/mime-type v) (:file/name v))))

(defn file-view
  ([v] (file-view {} v))
  ([opts v]
   (let [document-path (routes/cas-path nil v)
         file-name (or (:file/name v) (tr "Document"))]
     [ui/link (merge {:href document-path
                      :download file-name}
                     opts)
      [ui/icon {:width 12 :height 12} :file]
      file-name])))

(defn field-preview [v]
  [:div.flex.gap-2
   (tr (or (:ui/attr v) (:ui/prompt v)))
   (when (:ui/optional? v)
     (str " (" (tr "optional") ")"))])

(defn accessory-view
  ([accessory] (accessory-view {} accessory))
  ([opts accessory]
   (r/with-let [id (str (:accessory/kind accessory) "-" (random-uuid))]
     (js/console.log (pr-str "accessory: " opts))
     (let [expanded? @(deframe/!route-get-in [id :expanded?] (:ui/expanded? accessory))]
       [:div
        [:div.flex.items-center.justify-between
         [:div.flex.items-center.gap-2
          [:div.text-indigo-600.cursor-pointer.transition-all
           {:class (when expanded? ["rotate-90"])
            :on {:click [::deframe/route-update-in [id :expanded?] not]}
            :on-click #(deframe/route-update-in! [id :expanded?] not)}
           [ui/icon {:size 14} :chevron-right]]
          [:div.font-bold (tr (:accessory/kind accessory))]
          (when (:accessory/status accessory)
            [:div.text-gray-500
             (str "(" (tr (:accessory/status accessory)) ")")])]]
        (when expanded?
          [:div.text-gray-500.bg-slate-50.mt-1.ml-5.-mb-1.-mr-2
           [entity-view (merge opts {:nested? true :exclude [:ductile/id :ui/expanded?]}) accessory]])])
     (finally
       (deframe/route-dissoc! id)))))

(defn notification-view [opts notification]
  (let [slug (str "/compound/"
                  @deframe/!current-compound
                  "/"
                  (get {:email.bmw-unfall/entry "entry-protocol"
                        :email.arval/entry "entry-protocol"}
                       (:notification/kind notification))
                  "/"
                  (-> opts :context :ductile/id))]
    [:div.flex.items-center.gap-x-1.flex-wrap
     [ui/icon {:size 12} :mail]
     [:span
      (if (= :unprocessed (:notification/status notification))
        [:span.font-bold [ui/link {:href slug
                                   :target "_blank"} (tr (:notification/kind notification))]
         (str " " (tr :notification/processing))]
        [:<>
         (if (:cas/multihash notification)
           [ui/link
            {:href (str "/full-email/" (:ductile/id notification))
             :target "_blank"}
            [:span.font-bold (tr (:notification/kind notification))]]
           [:span.font-bold (tr (:notification/kind notification))])
         (str " "
              (if-let [sent-at (:notification/sent-at notification)]
                (str (tr :notification/sent-at) " " (time-format/->str {:style :short} sent-at))
                (str (tr :notification/is-sending))))])]
     (when (:notification/resendable? notification)
       (when-current-user-scope
        :scope/notifications
        [:div.flex.items-center.gap-x-1.hover:underline.text-indigo-600
         {:on-click #(deframe/call-lambda
                      'ductile.workflow/resend-notification
                      (-> @deframe/!current-route-fetch
                          :result
                          (select-keys [:ductile/id])
                          (assoc :ductile/notification notification)))}
         [ui/icon {:size 12} :reload]
         (tr "Resend")]))]))

(defn action-view [opts v]
  [:div
   [:div.flex.items-center.justify-between
    [:div.flex.item.center.gap-2
     {:class "text-[12px]"}
     [ui/link
      {:as :button
       :variant :primary
       :on-click (if-let [fields (:ui/fields v)]
                   #(view-stack/push-view! {:view/id (str (random-uuid))
                                            :view/type :form-direct
                                            :view/result (merge {:form/name ::form
                                                                 :ui/fields fields
                                                                 :form/handler (:action/lambda v)}
                                                                (select-keys opts [:ductile/id]))})
                   #(deframe/call-lambda
                     (:action/lambda v)
                     (merge v (select-keys opts [:ductile/id]))))}
      (:ui/label v)]]]])

(defn inspect-value
  ([v] [inspect-value {} v])
  ([{:as opts :keys [wrap exclude] :or {exclude [:ductile/id :ui/expanded?]}} v]
   (with-scope-protected-value v
     [(or wrap :<>)
      (cond
        (= :ui.view/event (:ui/view v)) [event-view v]
        (or (:ui/attr v) (:ui/prompt v)) [field-preview v]
        (:address/name v) [location-view v]
        (and (:service/code v)
             (not (:billing/price v))
             (not (:billing/prices v))) [service-view opts v]
        (:document/number v) [document-view v]
        (:invoice/invoice-number v) [invoice-view v]
        (:accessory/kind v) [accessory-view opts v]
        (:notification/kind v) [notification-view opts v]
        (:action/lambda v) [action-view opts v]
        (parking-spot? v) [parking-spot-view v]
        (transporter? v) [transporter-view v]
        (recorded-form? v) [pred-view/explain-pred (:form v)]
        (image? v) [image-view v]
        (file? v) [file-view v]
        (or (nil? v) (and (coll? v) (empty? v))) [blank-slate (tr "None")]
        (map? v) [entity-view (merge opts {:ui/layout :layout/nested :exclude exclude}) v]
        (inst? v) [:span.whitespace-nowrap.tabular-nums (time-format/->str v)]
        (and (vector? v) (= (count v) 2) (keyword? (second v))) [value-with-unit-view v]
        (or (sequential? v) (set? v)) (into [:div.flex.flex-col.gap-1] (map (partial inspect-value (merge opts {:wrap :div}))) v)
        (and (keyword? v) (= (namespace v) "optionality")) [ui/optionality-badge v]
        (keyword? v) (tr v)
        (number? v) [:span.tabular-nums (locale/format-number {:minimumFractionDigits 0 :maximumFractionDigits 6} v)]
        (boolean? v) (if (true? v) (tr "Yes") (tr "No"))
        (string? v) (into [:<>] (interpose [:br]) (str/split-lines v))
        :else (str v))])))

(defn trow [opts [k v]]
  (let [nested? (= :layout/nested (:ui/layout opts))
        standalone? (= :layout/standalone (:ui/layout opts))]
    [:tr.border-t.align-top.hover:bg-yellow-100
     {:class (cond
               (or nested? standalone?) ["first:border-t-0" "hover:bg-opacity-100"]
               :else ["hover:bg-opacity-50"])}
     [:th.text-left.border-r.px-2.py-1 {:class (:ui/header-classes opts)} [inspect-value opts k]]
     [:td.px-2.py-1 [inspect-value opts v]]]))

(defn entity-view-wrapper [inner opts]
  (let [nested? (= :layout/nested (:ui/layout opts))
        standalone? (= :layout/standalone (:ui/layout opts))]
    [(if standalone? :div.rounded-md.border.border-gray-300.bg-white :<>)
     {:class (when (:ui/scrolling? opts) ["overflow-auto"])}
     (if (empty? inner)
       [:div.text-xs
        {:class (when standalone? ["px-2" "py-1"])}
        [blank-slate (trow (assoc opts :pull/pattern []) "Empty")]]
       [:table.text-xs
        {:class (when nested? ["-ml-2" "-my-1"])
         :style {:width (if nested? "calc(100% + 2 * 0.5rem)" "100%")}}
        (into [:tbody] inner)])]))

(defn entity-list-view
  "Similar to `entity-view` but used when keys may not be unique.

  For instance, when the input is a vector of (timestamp, event) keyvals, the
  timestamp may be duplicated, so using `entity-view` which require a map as
  input won't allow for multiple events with the same timestamp"
  [opts entity-keyvals]
  [entity-view-wrapper
   (map (partial trow opts) entity-keyvals)
   opts])

(defn entity-view
  ([entity]
   [entity-view {} entity])
  ([opts entity]
   (let [inspected-map (apply dissoc entity (cond->> (conj (:exclude opts []) :db/id)
                                              (ui/debug-mode?) (remove #{:ductile/id :load/exited-with :load/entered-with})))]
     [entity-view-wrapper
      (if (empty? inspected-map)
        {}
        (if-let [pull-pattern (:pull/pattern opts)]
          (mapcat (fn [x]
                    (cond (keyword? x) (let [v (get inspected-map x ::missing)]
                                         (when (not= ::missing v)
                                           [(trow (assoc opts :pull/pattern [:*]) [x v])]))
                          (vector? x) (let [k-opts (into {} (map vec (partition 2 (drop 1 x))))
                                            k (or (:as k-opts)
                                                  (first x))
                                            v (get inspected-map k ::missing)]
                                        (when (or (:show-missing? k-opts) (not= ::missing v))
                                          [(trow (assoc opts :pull/pattern [:*]) [k v])]))
                          (map? x) (map (fn [[k nested-pull]]
                                          (trow (assoc opts :pull/pattern nested-pull) [k (get inspected-map k)]))
                                        x)))
                  (if (some #{:*} pull-pattern)
                    (let [before (take-while (complement #{:*}) pull-pattern)
                          after (rest (drop-while (complement #{:*}) pull-pattern))
                          remaining (clojure.set/difference (set (keys inspected-map))
                                                            (set (mapcat (fn [v] (cond (map? v)
                                                                                       (keys v)
                                                                                       (keyword? v)
                                                                                       [v]))
                                                                         before))
                                                            (set (mapcat (fn [v] (cond (map? v)
                                                                                       (keys v)
                                                                                       (keyword? v)
                                                                                       [v]))
                                                                         after)))]
                      (concat before
                              (if (sorted? inspected-map)
                                (filter remaining (keys inspected-map))
                                (sort-by tr remaining))
                              after))
                    pull-pattern))
          (map (partial trow opts)
               (cond->> inspected-map
                 (not (sorted? inspected-map))
                 (sort-by (comp tr key))))))
      opts])))

(defn transport-order-view
  ([kind transport-order] (transport-order-view {} kind transport-order))
  ([opts kind transport-order]
   [:div.rounded-md.border.border-gray-300.bg-white
    [:div.font-bold.text-sm.flex.gap-1.items-center.py-1.px-2.bg-indigo-50.rounded-t-md
     [ui/icon {:size 16} :document]
     (tr kind)]
    (if transport-order
      [entity-view
       (merge {:pull/pattern [:transport/name
                              :load/id
                              :load/number
                              :origin/location
                              :destination/location
                              :fahrzeug/destination
                              :*]}
              opts
              {:exclude [:ductile/id :external.bmw/spediteur :external.bmw/carrier-code :edi/provider :ductile/data]
               :ui/header-classes ["w-[240px]"]})
       transport-order]
      [:div.px-2.py-2.text-xs.text-gray-500.border-t
       (tr "No transport order data available.")])]))

(defn transport-view
  ([kind transport] (transport-view {} kind transport))
  ([opts kind transport]
   [:div.rounded-md.border.border-gray-300.bg-white
    {:data-testid (str "transport-" kind)}
    [:div.text-sm.flex.items-center.py-1.px-2.bg-orange-50.rounded-t-md.justify-between
     [:div.flex.items-center.font-bold.gap-1
      [ui/icon {:size 16} :truck]
      (tr kind)]
     (when transport
       [:div.text-xs
        [ui/link {:href (router/path '[compound :compound/name transports :ductile/id]
                                     {:ductile/id (:ductile/id transport)
                                      :compound/name @deframe/!current-compound})}
         (tr "Transport details")]])]
    (if transport
      [entity-view
       (merge opts
              {:pull/pattern [:transport/name
                              :load/id
                              :load/number
                              :transport/mode
                              :origin/location
                              :destination/location
                              :ductile/created-at
                              :transport/preparable-at
                              :transport/ready-at
                              :transport/finalized-at
                              :transport/deleted-at
                              :entry/datetime
                              :exit/datetime
                              :*]
               :exclude [:ductile/id
                         :external.bmw/carrier-code
                         :load/entered-with
                         :load/exited-with
                         :edi/provider
                         :ductile/data
                         :destination/ductile-ids]
               :ui/header-classes ["w-[240px]"]})
       transport]
      [:div.px-2.py-2.text-xs.text-gray-500.border-t
       (tr "No transport data available.")])]))

(defn blocked? [fahrzeug]
  (or (contains? (:fahrzeug/labels fahrzeug) :label/missing-information)
      (:vehicle/blocks fahrzeug)))

(defn blocked-badge [fahrzeug]
  (let [missing-information (contains? (:fahrzeug/labels fahrzeug) :label/missing-information)
        blocks (:vehicle/blocks fahrzeug)]
    [:div.px-2.py-1.bg-red-700.text-white.text-xs.uppercase.tracking-wide.font-bold.flex.items-center.gap-2.rounded.shadow-lg.border-gray-300
     [ui/icon {:size 24} :x-octagon]
     [:div (cond blocks (str/join ", " (map tr blocks))
                 missing-information (tr "missing information"))]]))

(defn fahrzeug-header
  [{:as fahrzeug :fahrzeug/keys [destination-error]}]
  (when (some #(get fahrzeug %) [:contract/name :color/description :make/name :vehicle/model :exit/transport-order :vehicle/vin :vehicle/engine-type :fahrzeug/destination-error])
    [:div
     [:div.flex.gap-3.items-center
      [:div.items-center.flex.border.border-gray-300.bg-white.rounded-lg.overflow-hidden.p-1.shadow-lg
       {:class ["w-[60px]" "h-[60px]"]}
       [ui/make-logo (:make/name fahrzeug)]]
      [:div
       (when (or (:make/name fahrzeug) (:vehicle/model fahrzeug) (:color/gradient-from fahrzeug))
         [:div.leading-tighter
          [:div (:make/name fahrzeug)]
          [:div.font-bold.flex.gap-1.items-center
           (:vehicle/model fahrzeug)
           (when (:color/gradient-from fahrzeug)
             [:div.w-6.rounded-full.shrink-0
              {:class "h-[0.85rem]"
               :style {:background (str "linear-gradient(to right, " (:color/gradient-from fahrzeug) " , " (:color/gradient-to fahrzeug) ")")}}])]])]]
     [:div.flex.gap-2.flex-wrap.empty:hidden.mt-2
      [engine-badge (assoc fahrzeug :ui/size 2)]
      (when (contains? fahrzeug :vehicle/non-drivable?)
        (if (:vehicle/non-drivable? fahrzeug)
          [ui/badge {:variant :red :size 2} (tr "Not drivable")]
          [ui/badge {:variant :green :size 2} (tr "Drivable")]))
      [package-badge (assoc fahrzeug :ui/size 2)]]
     (when destination-error
       [expected-error-view destination-error])]))

(def section-classes
  {:section/entry ["bg-orange-500"]
   :section/exit ["bg-green-500"]
   :section/active-services ["bg-pink-500"]
   :section/completed-services ["bg-blue-500"]
   :section/workflow-entity-locations ["bg-indigo-500"]})

(def section-icons
  {:section/entry :enter
   :section/exit :exit
   :section/active-services :box
   :section/scheduled-services :clock
   :section/completed-services :checkbox
   :section/events :list-bullet
   :section/invoices :reader
   :section/storage-billing :parking
   :section/images :image
   :section/edi :edi
   :section/workflow-entity-locations :map-pin
   :section/details :table
   :section/accessories :link-2
   :section/notifications :email})

(defn section-header [kind-or-opts & additional-content]
  (let [kind (if (map? kind-or-opts) (:kind kind-or-opts) kind-or-opts)
        opts (when (map? kind-or-opts) kind-or-opts)]
    [:div.flex.justify-between.mt-5.first:mt-0.mb-3.items-end
     [:div.flex.gap-2
      [:div.rounded-full.text-white.flex.shrink-0.items-center.justify-center.relative
       {:style {:width 24 :height 24 :top 3}
        :class (get section-classes kind ["bg-gray-500"])}
       (when-let [icon (get section-icons kind)]
         [ui/icon {:size 14} icon])]
      (into [:div
             [:div.text-xl.font-bold (tr kind)]]
            additional-content)]
     (:action opts)]))

(defn gate-event [event-type fahrzeug]
  [:div.text-sm
   (case event-type
     :gate/entered (if (:entry/datetime fahrzeug)
                     [:div.flex.items-center.gap-1.font-bold
                      [ui/icon {:size 16} :checkbox]
                      (tr "Entered at") ": " (time-format/->str (:entry/datetime fahrzeug))]
                     [:div.flex.items-center.gap-1.text-gray-500
                      [ui/icon {:size 16} :box]
                      (tr "not entered yet")])
     :gate/exited (if (:exit/datetime fahrzeug)
                    [:div.flex.items-center.gap-1.font-bold
                     [ui/icon {:size 16} :checkbox]
                     (tr "Exited at") ": " (time-format/->str (:exit/datetime fahrzeug))]
                    [:div.flex.items-center.gap-1.text-gray-500
                     [ui/icon {:size 16} :box]
                     (tr "not exited yet")]))])

(defn section-link [kind]
  [:a.flex.items-center.gap-2.hover:underline.text-indigo-600
   {:href (str "#" kind)
    :on-click (fn [event]
                (.preventDefault event)
                (.stopPropagation event)
                (when-let [el (js/document.getElementById (str kind))]
                  (.scrollIntoView el #js {:behavior "smooth"
                                           :block "start"})))}
   (when-let [icon (get section-icons kind)]
     [ui/icon {:size 18} icon])
   (tr kind)])

(defn section [kind & content]
  (into [:div.mt-7 {:id (str kind)}] content))

(defn items-count [items tr-strs]
  [:div.text-sm {:class (if (empty? items) "text-gray-500" "font-bold")}
   (trn tr-strs (count items))])

(defn listing
  ([items] [listing {} items])
  ([{:as opts :keys [actions]} items]
   (if (empty? items)
     [:div.rounded-md.border.border-gray-300.text-gray-500.px-2.py-1.text-xs
      (tr "No items available at the moment.")]
     [:div.rounded-md.border.border-gray-300
      [:table.text-xs
       {:style {:width "100%"}}
       (into
        [:tbody]
        (map (fn [v]
               [:tr.border-t.first:border-t-0.align-top.hover:bg-yellow-100
                {:class ["hover:bg-opacity-50"]}
                [:td.px-2.py-1 [inspect-value opts v]]]))
        items)]])))

(defn events-listing [events]
  [entity-list-view
   {:ui/layout :layout/standalone
    :ui/header-classes [:text-right]}
   (sort-by first
            (map (juxt :event/timestamp #(assoc % :ui/view :ui.view/event))
                 events))])

(defn edi-listing [messages]
  (if (seq messages)
    [entity-list-view
     {:ui/layout :layout/standalone
      :ui/scrolling? true}
     (sort-by first
              (map (juxt :edi_instructions/created_at
                         #(dissoc % :edi_instructions/instruction_raw))
                   messages))]
    [:div.text-gray-500.text-xs (tr "No EDI messages.")]))

(defn invoices-view [invoices]
  (if (some-> invoices :invoices :rows seq)
    [render/inspect (viewer/table (-> (:invoices invoices)
                                      (update :head #(take 5 %))
                                      (update :rows (fn [rows]
                                                      (map #(take 5 %) rows)))))]
    [:div.text-gray-500.text-xs (tr "No invoices found")]))


(defn storage-billing-view [{:keys [storage-billings]}]
  (if (some-> storage-billings :rows seq)
    [render/inspect (viewer/table storage-billings)]
    [:div.text-gray-500.text-xs (tr "No storage invoice found")]))

(defn toggle-new-fahrzeug-page! []
  (deframe/localstorage-assoc! :use-new-fahrzeug-page? (not @(deframe/!localstorage-get :use-new-fahrzeug-page?))))

(defn prev-next-links [fahrzeug]
  (when-let [{:fahrzeug/keys [prev next]} (not-empty (select-keys fahrzeug [:fahrzeug/prev :fahrzeug/next]))]
    [:div.flex.items-center.justify-between.my-3.text-sm
     [:div (when prev
             [ui/link {:class "flex items-center gap-1"
                       :href (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                          {:fahrzeug-id (:ductile/id prev)
                                           :compound-name (-> prev :ductile/location :compound/name)})}
              [ui/icon :chevron-left] (tr "Predecessor")])]
     [:div (when next
             [ui/link {:class "flex items-center gap-1"
                       :href (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                          {:fahrzeug-id (:ductile/id next)
                                           :compound-name (-> next :ductile/location :compound/name)})}
              (tr "Successor") [ui/icon :chevron-right]])]]))

(defn fahrzeug-page [fahrzeug]
  [:div.flex.flex-col.lg:flex-row
   (when @(deframe/!route-get-in [:fetch :result ::services-modal-open?])
     [ui/modal {:on-close #(do (deframe/route-dissoc-in! [:fetch :result ::services-modal-open?])
                               (deframe/route-dissoc-in! [:fetch :error]))}
      [new-service-form fahrzeug]])
   [:div.p-5.relative.bg-mauve2.border-b.lg:border-b-0.lg:border-r
    {:class "w-full lg:w-[400px] lg:shrink-0 lg:flex-shrink-0"}
    [:div.lg:sticky.lg:top-5.lg:left-5
     (when (blocked? fahrzeug)
       [:div.mb-2
        [blocked-badge fahrzeug]])
     [fahrzeug-header fahrzeug]
     [:div.mt-2
      [entity-view
       {:ui/layout :layout/standalone
        :pull/pattern [:vehicle/vin
                       :contract/names
                       :fahrzeug/parking-spot
                       :vehicle/license-plate]}
       fahrzeug]]
     [prev-next-links fahrzeug]
     [:div.flex.items-center.gap-2.mt-3.border-t.pt-3
      [blocks-dropdown fahrzeug {}]
      (when-current-user-scope
       #{:scope/developer
         :scope/edi}
       [ui/button {:href (router/path '[edi dashboard] {} (str "b64=" (base64u/encode {:search-params {:vehicle/vin #{(:vehicle/vin fahrzeug)}}})))
                   :size 1}
        (tr "Show EDI")])
      [barcode-button fahrzeug]]
     [:div.mt-3.pt-3.border-t.flex.flex-col.gap-2
      [section-link :section/entry]
      [section-link :section/exit]
      [section-link :section/active-services]
      [section-link :section/scheduled-services]
      [section-link :section/completed-services]
      [section-link :section/details]
      [section-link :section/accessories]
      [section-link :section/events]
      (when-current-user-scope
       :scope/billing
       [section-link :section/invoices])
      (when-current-user-scope
       :scope/billing
       [section-link :section/storage-billing])
      [section-link :section/images]
      [section-link :section/notifications]
      (when-current-user-scope
       :scope/compound-admin
       [section-link :section/workflow-entity-locations])
      [section-link :section/edi]]]]
   [:div.flex.flex-col.flex-auto.bg-white.w-full.px-4.lg:px-7.py-5
    {:class "lg:w-[calc(100vw-400px-2*1.25rem)]"}
    [:div.grid.grid-cols-1.lg:grid-cols-2.gap-5
     [section :section/entry
      [section-header :section/entry [gate-event :gate/entered fahrzeug]]]
     [section :section/exit
      [section-header :section/exit [gate-event :gate/exited fahrzeug]]]
     [transport-order-view :entry/transport-order (:entry/transport-order fahrzeug)]
     [transport-order-view :exit/transport-order (cond-> (merge (:exit/transport-order fahrzeug)

                                                                (select-keys fahrzeug [:fahrzeug/destination :ductile.workflow/dispatchable?]))
                                                   (:ductile.workflow/dispatchable? fahrzeug)
                                                   (assoc :dispo/available-datetime (:dispo/available-datetime fahrzeug)))]
     [transport-view :entry/transport (:entry/transport fahrzeug)]
     [transport-view :exit/transport (:exit/transport fahrzeug)]]
    [section :section/active-services
     [section-header {:kind :section/active-services
                      :action [ui/button
                               {:variant :secondary
                                :size 1
                                :on-click #(deframe/route-assoc-in! [:fetch :result ::services-modal-open?] true)}
                               (tr "Assign service")]}
      [items-count (:workflow/active-services fahrzeug)
       ["%1 active service" "%1 active services"]]]
     [listing {:context (select-keys fahrzeug [:ductile/id])} (:workflow/active-services fahrzeug)]]
    [section :section/scheduled-services
     [section-header :section/scheduled-services [items-count (:workflow/scheduled-services fahrzeug)
                                                  ["%1 scheduled service" "%1 scheduled services"]]]
     [listing (:workflow/scheduled-services fahrzeug)]]
    [section :section/completed-services
     [section-header :section/completed-services [items-count (:service.event/completed fahrzeug)
                                                  ["%1 completed service" "%1 completed services"]]]
     [listing (:service.event/completed fahrzeug)]]
    [section :section/accessories
     [section-header :section/accessories]
     [listing (:vehicle/accessories fahrzeug)]]
    [section :section/details
     [section-header :section/details]
     [entity-view
      {:ui/layout :layout/standalone
       :pull/pattern [:vehicle/vin
                      :ars/id
                      :contract/names
                      :vehicle/engine-type
                      :fuel/type
                      :make/name
                      :vehicle/model
                      :color/description
                      :external.bmw/color-code
                      :external.bmw/usage
                      :vehicle/dimensions
                      :vehicle/weight
                      :vehicle/new?
                      :vehicle/transport-mode-active?
                      :vehicle/dispatchable-without-call-off?
                      :dispo/available-datetime
                      :fahrzeug/storage-care-start-datetime
                      :vehicle/equipment
                      :vehicle/tires
                      :vehicle/rims
                      :vehicle/extra-tires
                      :vehicle/extra-rims

                      ;; TODO: get these from ductile.fahrzeug/contract-specific-attributes
                      :bca/vehicle-class
                      :car-on-sale/vehicle-class]}
      fahrzeug]]
    [section :section/events
     [section-header :section/events]
     [ui/loading-promise-view
      events-listing
      (http/$lambda 'ductile.workflow.engine/events-for-fahrzeug (:path-params @deframe/!current-route))]]
    (when-current-user-scope
     :scope/billing
     [section :section/invoices
      [section-header :section/invoices]
      [ui/loading-promise-view invoices-view (http/$lambda 'ductile.invoices/$list {:fahrzeug-id (:ductile/id fahrzeug)})]])
    (when-current-user-scope
     :scope/billing
     [section :section/storage-billing
      [section-header :section/storage-billing]
      [ui/loading-promise-view storage-billing-view (http/$lambda 'ductile.billing/$fahrzeug-storage-billings {:ductile/id (:ductile/id fahrzeug)})]])
    [section :section/images
     [section-header :section/images]
     [listing (->> (concat (services-with-photo-comments fahrzeug)
                           (services-with-photo-data fahrzeug))
                   (mapv #(select-keys % (conj photo-attrs :service/code :service/completed-at :ductile/comments)))
                   flatten)]]
    [section :section/notifications
     [section-header :section/notifications]
     [listing (concat (:ductile/notifications fahrzeug)
                      (mapcat :ductile/notifications (:fahrzeug/services fahrzeug)))]]
    (when-current-user-scope
     :scope/compound-admin
     [section :section/workflow-entity-locations
      [section-header :section/workflow-entity-locations]
      [ui/loading-promise-view workflow-entity-locations (http/$lambda 'ductile.workflow.engine/location-events-for-fahrzeug (:path-params @deframe/!current-route))]])
    [section :section/edi
     [section-header :section/edi]
     (when fahrzeug
       [ui/loading-promise-view edi-listing (http/$edi-vin-search (:vehicle/vin fahrzeug) (:ductile/id fahrzeug))])]
    (when-let [service @(deframe/!route-get ::confirm-cancelling-service)]
      [confirm-cancelling-service fahrzeug service])]])

(def demo-fahrzeug
  '{:vehicle.status/fuel-tank-liters 8.0,
    :workflow/services
    ({:service/code "Compound",
      :service/kind :ductile.workflow.dsl/group,
      :service/services
      ({:service/code "Posizionamento",
        :service/reparking true,
        :service/optionality :optionality/can,
        :ui/view ductile.ui.workflow/parking-view,
        :service/active? true}
       {:service/code "Ispezione Danni",
        :service/optionality :optionality/can,
        :ui/view ductile.ui.workflow/record-damages-with-photos-view,
        :service/active? true}),
      :ui/view ductile.ui.workflow/compound-view,
      :service/active? true,
      :service/optionality :optionality/must}),
    :vehicle/blocks [:block/by-compound],
    :contract/names ["Kia Distribuzione"],
    :vehicle/width-mm 1865,
    :vehicle/height-mm 1645,
    :compound/name "Lonato",
    :vehicle/weight 1557,
    :entry/expected-datetime
    #inst "2025-05-09T22:00:00.000000000-00:00",
    :vehicle/engine-type :engine.type/hybrid,
    :fahrzeug/damages
    [{:ductile/id #uuid "64412cee-2461-45ef-82f8-ca55bfac81af"}],
    :ductile/comments
    [{:ductile/id #uuid "55477135-53ac-4602-b385-3c5ddac1fea5",
      :cas/multihash
      "8VuA1iSPMqMZRzGgSHdoYFEUPYTmTE3M47EsmkLJrx7gz6xTRsV6zx5wmo2MJ4BioZ9RRQ9w4WDegLEzgj4S4T3Lst",
      :ductile/timestamp #inst "2025-05-10T09:06:09.189000000-00:00",
      :ductile.comment/kind :ductile.comment.kind/image}
     {:ductile/id #uuid "80d63fa3-faa0-4182-84cd-4cc44315371a",
      :cas/multihash
      "8VszASvV8KzAW4n7N2WoKAdGwKfzpAYkw8EyoKw1NWWb5XKZXGJQw3z6AYRGP2SJjntfesSHWSmCww4uVykJQDnfqL",
      :ductile/timestamp #inst "2025-05-10T09:06:16.525000000-00:00",
      :ductile.comment/kind :ductile.comment.kind/image}
     {:ductile/id #uuid "670c7e9e-7342-43e9-a13f-b7a44ff5fd94",
      :cas/multihash
      "8Vu2KTu1H1xosQfQgUB1B6bux3yqdcbGC3J4Dk7Y1yQoENSDAYPQSrUppcb25iTsqBebPqLTLE5LzegwoTYGYeGowZ",
      :ductile/timestamp #inst "2025-05-10T09:06:22.352000000-00:00",
      :ductile.comment/kind :ductile.comment.kind/image}],
    :entry/transport-order
    {:destination/location
     {:compound/name "Lonato",
      :location/longitude 10.47789,
      :external.kia/location-codes ["019" "09254" "1160" "G"],
      :address/country {:country/code "ITA"},
      :location/types [:location.type/compound],
      :address/province-code "BS",
      :ductile/id #uuid "0530e959-5c3a-4ca1-9639-08224e17af7e",
      :address/postcode "25017",
      :compound/company {:company/name "ARS Service S.r.l."},
      :address/street "Via Salera, 2",
      :address/name "ARS Service SRL Lonato",
      :address/city "Lonato",
      :location/latitude 45.45422},
     :transport/mode :mode/truck,
     :transport/name "BTR01",
     :ductile/id #uuid "69265f27-a58f-497b-a8ca-3c73a840fe05"},
    :service.event/completed
    [{:service/code "Scarico treno",
      :service/completed-at #inst "2025-05-10T08:53:39.099000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "aad6a4f5-6f70-4c1d-a1b5-076193a833b4",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Controllo Accessori",
      :service/completed-at #inst "2025-05-10T08:53:46.439000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "78d70ada-fc8d-4bd3-ab0f-9e53b3d541ab",
      :vehicle/equipment
      ["Antenna"
       "Cappelliera"
       "Doppia chiave"
       "Kit gonfiaggio"
       "Manuale uso e manutenzione"
       "Porta targa anteriore"
       "Tools"],
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Ispezione Danni",
      :service/completed-at #inst "2025-05-10T08:53:48.561000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "da87c119-eca1-406e-92b6-437963019413",
      :service/result {:damaged? false},
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T08:53:58.520000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "6309bbf7-ae23-4e2b-87c4-0b3df26fe670",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Ispezione Danni",
      :ductile/comments
      [{:ductile/id #uuid "55477135-53ac-4602-b385-3c5ddac1fea5",
        :cas/multihash
        "8VuA1iSPMqMZRzGgSHdoYFEUPYTmTE3M47EsmkLJrx7gz6xTRsV6zx5wmo2MJ4BioZ9RRQ9w4WDegLEzgj4S4T3Lst",
        :ductile/timestamp #inst "2025-05-10T09:06:09.189000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}
       {:ductile/id #uuid "80d63fa3-faa0-4182-84cd-4cc44315371a",
        :cas/multihash
        "8VszASvV8KzAW4n7N2WoKAdGwKfzpAYkw8EyoKw1NWWb5XKZXGJQw3z6AYRGP2SJjntfesSHWSmCww4uVykJQDnfqL",
        :ductile/timestamp #inst "2025-05-10T09:06:16.525000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}
       {:ductile/id #uuid "670c7e9e-7342-43e9-a13f-b7a44ff5fd94",
        :cas/multihash
        "8Vu2KTu1H1xosQfQgUB1B6bux3yqdcbGC3J4Dk7Y1yQoENSDAYPQSrUppcb25iTsqBebPqLTLE5LzegwoTYGYeGowZ",
        :ductile/timestamp #inst "2025-05-10T09:06:22.352000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}],
      :service/completed-at #inst "2025-05-10T09:06:24.101000000-00:00",
      :event/kind :service.event/completed,
      :ductile/notifications
      [{:ductile/id #uuid "67c57680-d0bd-4629-b447-092d8c59fe0b",
        :notification/kind :email/fahrzeug-damages,
        :notification/sent-at
        #inst "2025-05-10T09:11:02.534000000-00:00"}],
      :ductile/id #uuid "64412cee-2461-45ef-82f8-ca55bfac81af",
      :service/result {:damaged? true},
      :ductile/user {:user/email "movi3@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T09:27:00.652000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "b332d90a-f792-44d4-a851-fc5578370def",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T09:27:09.483000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "39cdfd9b-1206-48eb-91da-ff79fb28f881",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Block",
      :service/completed-at #inst "2025-05-12T07:54:21.498000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "653afeab-b9d3-4faf-a53a-9c3ae057b3be",
      :ductile/created-at #inst "2025-05-12T07:54:21.497000000-00:00",
      :ductile/user {:user/email "manuel.ferrari@ars-altmann.it"}}],
    :customer/name "KIA Italia SRL",
    :vehicle/vin "U5YPX81GBSL255184",
    :fahrzeug/contract
    {:contract/customer {:customer/name "KIA Italia SRL"}},
    :vehicle/model "Sportage (NQ5) HEV/PHEV",
    :fahrzeug/assignable-services
    #{{:service/code "Controllo Batteria 12V"}
      {:service/code "Caricamento batteria HV"}
      {:service/code "PDI"}
      {:service/code "Montaggio targa"}
      {:service/code "Lavaggio"}
      {:service/code "Self Pickup"}
      {:service/code "Installazione BB + Appaiamento"}
      {:service/code "PDI - Inserimento libretti"}
      {:service/code "Controllo Accessori"}
      {:service/code "Inserimento Kit / Accessori"}
      {:service/code "Spedizione materiale"}
      {:service/code "PDI - Inserimento tappetini"}
      {:service/code "Controllo Batteria HV"}
      {:service/code "Lavaggio PDI"}
      {:service/code "Caricamento batteria"}},
    :service.event/canceled (),
    :storage/entry-datetime #inst "2025-05-10T08:53:50.096000000-00:00",
    :parking-zone/name "A",
    :vehicle/engine-type-from-model-info? true,
    :country/code "ITA",
    :parking-spot/zone "A",
    :contract/framework-contract "KIA",
    :ductile/notifications
    [{:ductile/id #uuid "3dbb26e3-8295-4265-983b-9db578f2560b",
      :notification/kind :edi.kia/fpr-entry-announcement}
     {:ductile/id #uuid "7bf89ff7-4e43-44ab-ad55-e201f9e7dd21",
      :notification/kind :edi.kia/compound-entry-announcement}
     {:ductile/id #uuid "6129c800-054c-4aa1-a84d-25f635865798",
      :notification/kind :edi.kia/block}],
    :vehicle.status/mineral-oil-liters 10.8,
    :vehicle/length-mm 4515,
    :workflow/active-services
    [{:service/code "Posizionamento",
      :service/reparking true,
      :service/optionality :optionality/can,
      :ui/view ductile.ui.workflow/parking-view,
      :service/active? true}
     {:service/code "Ispezione Danni",
      :service/optionality :optionality/can,
      :ui/view ductile.ui.workflow/record-damages-with-photos-view,
      :service/active? true}],
    :fuel/type :fuel.type/petrol,
    :ars/id "OAKBZDG2000120001200012000698377",
    :external.kia/model-code "JYW5D5G1FEV1HH0N4",
    :color/description "(1K) PEARL BLACK M",
    :workflow/active-service-codes
    #{"Ispezione Danni" "Posizionamento"},
    :ductile/id #uuid "23c01772-8dee-41ea-8408-814b6bb6968a",
    :fahrzeug/vehicle
    {:vehicle/model-info
     {:ductile/data
      {:kia.pdi/carpets #{{:type "Velours", :code "CJ143ADE00GL"}},
       :kia/accessories
       #{"Manuale uso e manutenzione"
         "Antenna"
         "Cappelliera"
         "Porta targa anteriore"
         "Tools"
         "Doppia chiave"
         "Kit gonfiaggio"},
       :kia/gpl-accessories #{},
       :edi.kia/model "JYW5D5G1FE"},
      :vehicle/cylinder-capacity 1598},
     :vehicle/import-value 2663700,
     :vehicle/color {:external.kia/color-code "1K"},
     :vehicle/import-document-number "E2412C11VCE-S5Z4-G"},
    :parking-spot/row 3,
    :ductile.workflow/matching-exited-fahrzeug nil,
    :vehicle/equipment
    ["Antenna"
     "Cappelliera"
     "Doppia chiave"
     "Kit gonfiaggio"
     "Manuale uso e manutenzione"
     "Porta targa anteriore"
     "Tools"],
    :workflow/code "base-workflow",
    :workflow/open-service-codes nil,
    :fahrzeug/parking-spot
    {:parking-spot/row 3,
     :parking-spot/place 4,
     :parking-spot/zone "A"},
    :vehicle/dimensions "4515×1865×1645",
    :make/name "Kia",
    :ductile/location
    {:location/longitude 10.47789,
     :external.kia/location-codes ["019" "09254" "1160" "G"],
     :location/types [:location.type/compound],
     :address/province-code "BS",
     :ductile/id #uuid "0530e959-5c3a-4ca1-9639-08224e17af7e",
     :address/postcode "25017",
     :compound/company {:company/name "ARS Service S.r.l."},
     :address/street "Via Salera, 2",
     :address/name "ARS Service SRL Lonato",
     :address/city "Lonato",
     :location/latitude 45.45422},
    :entry/datetime #inst "2025-05-10T08:53:50.096000000-00:00",
    :fahrzeug/services
    ({:service/code "Scarico treno",
      :service/completed-at #inst "2025-05-10T08:53:39.099000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "aad6a4f5-6f70-4c1d-a1b5-076193a833b4",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Controllo Accessori",
      :service/completed-at #inst "2025-05-10T08:53:46.439000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "78d70ada-fc8d-4bd3-ab0f-9e53b3d541ab",
      :vehicle/equipment
      ["Antenna"
       "Cappelliera"
       "Doppia chiave"
       "Kit gonfiaggio"
       "Manuale uso e manutenzione"
       "Porta targa anteriore"
       "Tools"],
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Ispezione Danni",
      :service/completed-at #inst "2025-05-10T08:53:48.561000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "da87c119-eca1-406e-92b6-437963019413",
      :service/result {:damaged? false},
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T08:53:58.520000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "6309bbf7-ae23-4e2b-87c4-0b3df26fe670",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Ispezione Danni",
      :ductile/comments
      [{:ductile/id #uuid "55477135-53ac-4602-b385-3c5ddac1fea5",
        :cas/multihash
        "8VuA1iSPMqMZRzGgSHdoYFEUPYTmTE3M47EsmkLJrx7gz6xTRsV6zx5wmo2MJ4BioZ9RRQ9w4WDegLEzgj4S4T3Lst",
        :ductile/timestamp #inst "2025-05-10T09:06:09.189000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}
       {:ductile/id #uuid "80d63fa3-faa0-4182-84cd-4cc44315371a",
        :cas/multihash
        "8VszASvV8KzAW4n7N2WoKAdGwKfzpAYkw8EyoKw1NWWb5XKZXGJQw3z6AYRGP2SJjntfesSHWSmCww4uVykJQDnfqL",
        :ductile/timestamp #inst "2025-05-10T09:06:16.525000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}
       {:ductile/id #uuid "670c7e9e-7342-43e9-a13f-b7a44ff5fd94",
        :cas/multihash
        "8Vu2KTu1H1xosQfQgUB1B6bux3yqdcbGC3J4Dk7Y1yQoENSDAYPQSrUppcb25iTsqBebPqLTLE5LzegwoTYGYeGowZ",
        :ductile/timestamp #inst "2025-05-10T09:06:22.352000000-00:00",
        :ductile.comment/kind :ductile.comment.kind/image}],
      :service/completed-at #inst "2025-05-10T09:06:24.101000000-00:00",
      :event/kind :service.event/completed,
      :ductile/notifications
      [{:ductile/id #uuid "67c57680-d0bd-4629-b447-092d8c59fe0b",
        :notification/kind :email/fahrzeug-damages,
        :notification/sent-at
        #inst "2025-05-10T09:11:02.534000000-00:00"}],
      :ductile/id #uuid "64412cee-2461-45ef-82f8-ca55bfac81af",
      :service/result {:damaged? true},
      :ductile/user {:user/email "movi3@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T09:27:00.652000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "b332d90a-f792-44d4-a851-fc5578370def",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Posizionamento",
      :service/completed-at #inst "2025-05-10T09:27:09.483000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "39cdfd9b-1206-48eb-91da-ff79fb28f881",
      :ductile/user {:user/email "movi24@lonato.ductile.de"}}
     {:service/code "Block",
      :service/completed-at #inst "2025-05-12T07:54:21.498000000-00:00",
      :event/kind :service.event/completed,
      :ductile/id #uuid "653afeab-b9d3-4faf-a53a-9c3ae057b3be",
      :ductile/created-at #inst "2025-05-12T07:54:21.497000000-00:00",
      :ductile/user {:user/email "manuel.ferrari@ars-altmann.it"}}),
    :parking-spot/place 4,
    :entry/mode :mode/train,
    :contract/name "Kia Distribuzione",
    :parking-spot "A/3/4"})

(defscene completed-services []
  [listing (:service.event/completed demo-fahrzeug)])

(defscene completed-services-expanded []
  [listing (map #(assoc % :ui/expanded? true) (:service.event/completed demo-fahrzeug))])

(defscene entity []
  [:div.flex.flex-col.gap-4
   [entity-view {}]
   [entity-view {:ui/layout :layout/standalone} {}]
   [entity-view
    {:ui/layout :layout/standalone}
    demo-fahrzeug]])

(defn try-feature-button [opts & content]
  [:div.flex.justify-end.mb-2.gap-1
   [ui/badge {:size 1 :variant :orange} "BETA"]
   (into
    [:span.text-indigo-600.hover:underline.cursor-pointer
     (merge {:class ["text-[11px]"]} opts)]
    content)])

(defn render-vehicle-overview [_result _match]
  (let [uses-new-page? @(deframe/!localstorage-get :use-new-fahrzeug-page?)]
    [:div.-mt-2
     [try-feature-button
      {:on-click toggle-new-fahrzeug-page!}
      (if uses-new-page?
        (tr "Back to the old vehicle page!")
        (tr "Try the Beta version of the new vehicle page now!"))]
     (if uses-new-page?
       [fahrzeug-page (:result @deframe/!current-route-fetch)]
       [show-fahrzeug (:result @deframe/!current-route-fetch)])]))
